import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  collection,
  collectionGroup,
  documentId,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  startAfter,
  where,
} from 'firebase/firestore';
import { db } from '../../firebase';
import './Sightings.css';
import {
  buildHighlightEntry,
  formatCountWithSpecies,
  formatPercent,
  formatTime,
  normalizeDate,
} from '../../utils/highlights';
import useAuthStore from '../../stores/authStore';
import { buildLocationSet, normalizeLocationId } from '../../utils/location';
import { trackButton, trackEvent } from '../../utils/analytics';
import usePageTitle from '../../hooks/usePageTitle';
import useSightingDeepLink from '../../utils/sightings/useSightingDeepLink';
import { isSightingVisibleOrLinked } from '../../utils/sightings/deepLink';
import { FiDownload, FiEdit2, FiStar, FiTrash2 } from 'react-icons/fi';
import { FaFacebookF, FaStar } from 'react-icons/fa';
import {
  applySightingCorrection,
  describeSpeciesChange,
  buildCorrectionNote,
} from '../../utils/sightings/corrections';
import { hydrateCameraDraft, hydrateClientDraft } from '../../utils/adminSettings';
import { downloadEntryMedia } from '../../utils/media';
import { areEquivalentSpecies } from '../../utils/species';
import {
  applySightingHighlight,
  getHighlightStateKey,
} from '../../utils/sightings/highlights';
import {
  getMotionTrackUrl,
  getSightingVideoPlaybackRate,
  matchesSightingMediaFilter,
  MOTION_TRACK_PLAYBACK_RATE,
} from '../../utils/sightings/mediaFilter';
import {
  DEFAULT_WHATSAPP_CLIP_SELECTION,
  formatWhatsAppClipSelection,
  promptForWhatsAppClipSelection,
} from '../../utils/whatsappClip';
import { reviewAlertSpeciesSpelling } from '../../utils/alertSpellCheck';
import FacebookVideoPublishModal from '../../components/FacebookVideoPublishModal';
import {
  getFacebookConnectionErrorMessage,
  loadFacebookPageConnection,
} from '../../utils/FB/facebookPageConnection';
const SIGHTINGS_PAGE_SIZE = 50;
const SIGHTINGS_PAGE_STORAGE_KEY = 'tingvision:sightings:page';
const LOOKUP_BATCH_SIZE = 10;
const SEND_WHATSAPP_ENDPOINT =
  process.env.REACT_APP_SEND_WHATSAPP_ENDPOINT ||
  'https://send-manual-whatsapp-alert-186628423921.us-central1.run.app';
const DELETE_SIGHTING_ENDPOINT =
  process.env.REACT_APP_DELETE_SIGHTING_ENDPOINT ||
  'https://delete-sighting-media-186628423921.us-central1.run.app';

const formatDate = (value) => {
  if (!value) return '';
  try {
    return value.toLocaleDateString();
  } catch (error) {
    return '';
  }
};

const formatTimestampLabel = (value) => {
  if (!(value instanceof Date) || Number.isNaN(value.getTime())) {
    return '';
  }

  const now = new Date();
  const timeLabel = formatTime(value);
  if (!timeLabel) {
    return '';
  }

  const todayKey = now.toDateString();
  const valueKey = value.toDateString();
  if (valueKey === todayKey) {
    return `Today at ${timeLabel}`;
  }

  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (valueKey === yesterday.toDateString()) {
    return `Yesterday at ${timeLabel}`;
  }

  const dateLabel = formatDate(value);
  if (!dateLabel) {
    return '';
  }

  return `${dateLabel} at ${timeLabel}`;
};

const formatCompactTimestampLabel = (value) => {
  if (!(value instanceof Date) || Number.isNaN(value.getTime())) {
    return '';
  }

  const now = new Date();
  const timeLabel = formatTime(value);
  if (!timeLabel) {
    return '';
  }

  const todayKey = now.toDateString();
  const valueKey = value.toDateString();
  if (valueKey === todayKey) {
    return `Today ${timeLabel}`;
  }

  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (valueKey === yesterday.toDateString()) {
    return `Yesterday ${timeLabel}`;
  }

  const dateLabel = formatDate(value);
  if (!dateLabel) {
    return '';
  }

  return `${dateLabel} ${timeLabel}`;
};

const isDateToday = (value) => {
  if (!(value instanceof Date) || Number.isNaN(value.getTime())) {
    return false;
  }
  const today = new Date();
  return value.toDateString() === today.toDateString();
};

const getCaptureTimestamp = (entry) => {
  if (!entry) return null;
  const candidate = entry.spottedAt || entry.createdAt || null;
  if (!(candidate instanceof Date) || Number.isNaN(candidate.getTime())) {
    return null;
  }
  return candidate;
};

const sanitizePageIndex = (value) => {
  const parsed = Number.parseInt(value, 10);
  if (Number.isNaN(parsed) || parsed < 0) {
    return 0;
  }
  return parsed;
};

const readStoredSightingsPageIndex = () => {
  if (typeof window === 'undefined') {
    return 0;
  }

  try {
    return sanitizePageIndex(window.sessionStorage.getItem(SIGHTINGS_PAGE_STORAGE_KEY));
  } catch (error) {
    return 0;
  }
};

const writeStoredSightingsPageIndex = (pageIndex) => {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.sessionStorage.setItem(SIGHTINGS_PAGE_STORAGE_KEY, String(sanitizePageIndex(pageIndex)));
  } catch (error) {
    // Ignore storage write failures and fall back to page 1 on the next hard refresh.
  }
};

const pickFirstSource = (...sources) => sources.find((src) => typeof src === 'string' && src.length > 0) || null;
const toAnalyticsError = (value) => String(value || '').slice(0, 120);

const chunkItems = (items, chunkSize) => {
  if (!Array.isArray(items) || items.length === 0 || chunkSize <= 0) {
    return [];
  }

  const chunks = [];
  for (let index = 0; index < items.length; index += chunkSize) {
    chunks.push(items.slice(index, index + chunkSize));
  }
  return chunks;
};

const normalizeMegadetectorVerify = (value) => {
  if (!value || typeof value !== 'object') {
    return null;
  }
  return value;
};

const isFiniteNumber = (value) => typeof value === 'number' && !Number.isNaN(value);

const hashStringToHue = (value) => {
  if (typeof value !== 'string' || value.length === 0) {
    return 32;
  }
  let hash = 0;
  for (let i = 0; i < value.length; i += 1) {
    hash = (hash * 31 + value.charCodeAt(i)) % 360;
  }
  return hash;
};

const getSpeciesColor = (label) => {
  const hue = hashStringToHue(label);
  return `hsla(${hue}, 78%, 60%, 0.95)`;
};

const pickEntryDebugValue = (entry, key) => {
  const speciesDoc = entry?.meta?.speciesDoc;
  const parentDoc = entry?.meta?.parentDoc;
  if (speciesDoc && speciesDoc[key] !== undefined && speciesDoc[key] !== null) {
    return speciesDoc[key];
  }
  if (parentDoc && parentDoc[key] !== undefined && parentDoc[key] !== null) {
    return parentDoc[key];
  }
  return null;
};

const resolveEntryTrigger = (entry) => entry?.meta?.parentDoc?.trigger || entry?.meta?.speciesDoc?.trigger || null;

const resolveEntryGeminiReview = (entry) => {
  const review = pickEntryDebugValue(entry, 'geminiReview');
  if (!review || typeof review !== 'object' || Array.isArray(review)) {
    return null;
  }
  return review;
};

const resolveGeminiPrediction = (review) => {
  if (!review) {
    return null;
  }

  const directCandidates = [
    review.parsedSpecies,
    review.normalizedSpecies,
    review.species,
    review.prediction,
  ];
  const directPrediction = directCandidates.find(
    (value) => typeof value === 'string' && value.trim(),
  );
  if (directPrediction) {
    return directPrediction.trim();
  }

  if (typeof review.rawResponse !== 'string' || !review.rawResponse.trim()) {
    return null;
  }

  const rawResponse = review.rawResponse.trim();
  try {
    const parsed = JSON.parse(rawResponse);
    if (typeof parsed?.species === 'string' && parsed.species.trim()) {
      return parsed.species.trim();
    }
  } catch {
    // Older reviews may contain plain text instead of structured JSON.
  }
  return rawResponse;
};

const formatDifferentGeminiPrediction = (species, review) => {
  const prediction = resolveGeminiPrediction(review);
  if (!prediction) {
    return null;
  }

  if (areEquivalentSpecies(species, prediction)) {
    return null;
  }

  return prediction.charAt(0).toLocaleUpperCase() + prediction.slice(1);
};

const buildGeminiDebugFields = (review) => {
  if (!review) {
    return [{ key: 'GEMINI_PREDICTION', value: 'Not recorded' }];
  }

  const prediction = resolveGeminiPrediction(review);
  const selectedFrame = review.finalSelectedFrameIndex ?? review.returnedBestFrameIndex;
  const submittedFrameCount = normalizeNumericValue(review.submittedFrameCount);
  const selectedFrameLabel = selectedFrame !== null && selectedFrame !== undefined
    ? `${selectedFrame}${submittedFrameCount ? ` of ${submittedFrameCount}` : ''}`
    : null;

  return [
    { key: 'GEMINI_PREDICTION', value: prediction || 'Unavailable' },
    ...(selectedFrameLabel
      ? [{ key: 'GEMINI_BEST_FRAME', value: selectedFrameLabel }]
      : []),
    ...(typeof review.speciesAgreement === 'boolean'
      ? [{ key: 'GEMINI_AGREES_WITH_YOLO', value: review.speciesAgreement }]
      : []),
    ...(typeof review.status === 'string' && review.status.trim()
      ? [{ key: 'GEMINI_STATUS', value: review.status.trim() }]
      : []),
    ...(typeof review.model === 'string' && review.model.trim()
      ? [{ key: 'GEMINI_MODEL', value: review.model.trim() }]
      : []),
    ...(typeof review.error === 'string' && review.error.trim()
      ? [{ key: 'GEMINI_ERROR', value: review.error.trim() }]
      : []),
  ];
};

const normalizePresetValue = (value) => {
  if (value === null || value === undefined) {
    return null;
  }

  if (typeof value === 'number') {
    return Number.isNaN(value) ? null : value;
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) {
      return null;
    }
    const numeric = Number(trimmed);
    return Number.isFinite(numeric) ? numeric : trimmed;
  }

  return value;
};

const resolveEntryPresetValue = (entry, rawTrigger) => {
  const rawPreset = pickEntryDebugValue(entry, 'preset');
  const triggerPreset = rawTrigger?.preset;
  const rawPresetId = pickEntryDebugValue(entry, 'preset_id');
  const triggerPresetId = rawTrigger?.preset_id;
  return normalizePresetValue(rawPreset)
    ?? normalizePresetValue(triggerPreset)
    ?? normalizePresetValue(rawPresetId)
    ?? normalizePresetValue(triggerPresetId);
};

const resolveTopBoxConfidence = (boxes) => {
  if (!Array.isArray(boxes)) {
    return null;
  }

  return boxes.reduce((bestConfidence, box) => {
    const confidence = normalizeNumericValue(
      box?.conf
      ?? box?.confidence
      ?? box?.score
      ?? box?.maxConf
      ?? box?.max_conf,
    );

    if (confidence === null) {
      return bestConfidence;
    }

    return bestConfidence === null || confidence > bestConfidence
      ? confidence
      : bestConfidence;
  }, null);
};

const resolveEntryMlConfidence = (entry, rawTrigger) => {
  const confidenceCandidates = [
    entry?.maxConf,
    pickEntryDebugValue(entry, 'maxConf'),
    pickEntryDebugValue(entry, 'max_conf'),
    pickEntryDebugValue(entry, 'ml_confidence'),
    pickEntryDebugValue(entry, 'prediction_confidence'),
    pickEntryDebugValue(entry, 'ml_prob'),
    pickEntryDebugValue(entry, 'confidence'),
    rawTrigger?.maxConf,
    rawTrigger?.max_conf,
    rawTrigger?.ml_confidence,
    rawTrigger?.prediction_confidence,
    rawTrigger?.ml_prob,
    rawTrigger?.tour_classifier_prob,
    rawTrigger?.confidence,
    rawTrigger?.conf,
  ];

  for (const candidate of confidenceCandidates) {
    const confidence = normalizeNumericValue(candidate);
    if (confidence !== null) {
      return confidence;
    }
  }

  return resolveTopBoxConfidence(pickEntryDebugValue(entry, 'topBoxes'))
    ?? resolveTopBoxConfidence(rawTrigger?.topBoxes)
    ?? resolveTopBoxConfidence(rawTrigger?.boxes)
    ?? resolveTopBoxConfidence(rawTrigger?.bboxes);
};

const resolveEntryMlBackend = (entry, rawTrigger) => {
  const backendCandidates = [
    pickEntryDebugValue(entry, 'ml_backend'),
    rawTrigger?.ml_backend,
    rawTrigger?.backend,
  ];

  for (const candidate of backendCandidates) {
    if (typeof candidate !== 'string') {
      continue;
    }
    const backend = candidate.trim();
    if (backend) {
      return backend;
    }
  }

  return null;
};

const HighlightIcon = ({ filled = false }) => (filled ? <FaStar /> : <FiStar />);

const normalizeBoundingBoxes = (rawBoxes, frameW, frameH, source = 'debug') => {
  if (!Array.isArray(rawBoxes)) {
    return [];
  }

  const toPercent = (value, dimension) => {
    if (!isFiniteNumber(value)) {
      return null;
    }
    if (isFiniteNumber(dimension) && dimension > 0 && value > 1.01) {
      return (value / dimension) * 100;
    }
    if (value <= 1.01) {
      return value * 100;
    }
    return value;
  };

  return rawBoxes
    .map((box) => {
      let x = null;
      let y = null;
      let w = null;
      let h = null;

      if (Array.isArray(box) && box.length >= 4) {
        [x, y, w, h] = box;
      } else if (Array.isArray(box?.bbox) && box.bbox.length >= 4) {
        [x, y, w, h] = box.bbox;
      } else if (box && typeof box === 'object') {
        if (isFiniteNumber(box.x) && isFiniteNumber(box.y)) {
          x = box.x;
          y = box.y;
          if (isFiniteNumber(box.w) && isFiniteNumber(box.h)) {
            w = box.w;
            h = box.h;
          } else if (isFiniteNumber(box.width) && isFiniteNumber(box.height)) {
            w = box.width;
            h = box.height;
          }
        }

        if (x === null && isFiniteNumber(box.left) && isFiniteNumber(box.top)) {
          if (isFiniteNumber(box.right) && isFiniteNumber(box.bottom)) {
            x = box.left;
            y = box.top;
            w = box.right - box.left;
            h = box.bottom - box.top;
          }
        }

        if (x === null && isFiniteNumber(box.x1) && isFiniteNumber(box.y1)) {
          if (isFiniteNumber(box.x2) && isFiniteNumber(box.y2)) {
            x = box.x1;
            y = box.y1;
            w = box.x2 - box.x1;
            h = box.y2 - box.y1;
          }
        }
      }

      if (!isFiniteNumber(x) || !isFiniteNumber(y) || !isFiniteNumber(w) || !isFiniteNumber(h)) {
        return null;
      }

      const left = toPercent(x, frameW);
      const top = toPercent(y, frameH);
      const width = toPercent(w, frameW);
      const height = toPercent(h, frameH);

      if (![left, top, width, height].every(isFiniteNumber)) {
        return null;
      }

      const confidence = isFiniteNumber(box?.conf)
        ? box.conf
        : (isFiniteNumber(box?.score) ? box.score : null);
      const label = typeof box?.label === 'string'
        ? box.label
        : (typeof box?.species === 'string' ? box.species : null);

      return {
        left: Math.max(0, Math.min(left, 100)),
        top: Math.max(0, Math.min(top, 100)),
        width: Math.max(0, Math.min(width, 100)),
        height: Math.max(0, Math.min(height, 100)),
        confidence,
        label,
        source,
      };
    })
    .filter(Boolean);
};

const resolveMegadetectorBoxes = (megadetectorVerify, frameW, frameH) => {
  if (!megadetectorVerify || typeof megadetectorVerify !== 'object') {
    return [];
  }

  const rawBoxes =
    megadetectorVerify.detections
    || megadetectorVerify.boxes
    || megadetectorVerify.bboxes
    || megadetectorVerify.bbox
    || null;

  const normalized = normalizeBoundingBoxes(rawBoxes, frameW, frameH, 'megadetector');
  return normalized.map((box) => ({
    ...box,
    label: box.label || 'MegaDetector',
  }));
};

const calculateIoU = (boxA, boxB) => {
  const left = Math.max(boxA.left, boxB.left);
  const top = Math.max(boxA.top, boxB.top);
  const right = Math.min(boxA.left + boxA.width, boxB.left + boxB.width);
  const bottom = Math.min(boxA.top + boxA.height, boxB.top + boxB.height);
  const intersectionWidth = right - left;
  const intersectionHeight = bottom - top;
  if (intersectionWidth <= 0 || intersectionHeight <= 0) {
    return 0;
  }
  const intersectionArea = intersectionWidth * intersectionHeight;
  const areaA = boxA.width * boxA.height;
  const areaB = boxB.width * boxB.height;
  const unionArea = areaA + areaB - intersectionArea;
  if (unionArea <= 0) {
    return 0;
  }
  return intersectionArea / unionArea;
};

const resolveEntryDebugBoxes = (entry) => {
  const frameW = pickEntryDebugValue(entry, 'frameW');
  const frameH = pickEntryDebugValue(entry, 'frameH');
  const debugBoxes =
    pickEntryDebugValue(entry, 'topBoxes')
    || pickEntryDebugValue(entry, 'boxes')
    || pickEntryDebugValue(entry, 'bboxes')
    || entry?.trigger?.topBoxes
    || entry?.trigger?.boxes
    || entry?.trigger?.bboxes
    || null;
  const megadetectorVerify = resolveMegadetectorVerify(
    entry?.megadetectorVerify,
    pickEntryDebugValue(entry, 'megadetector_verify'),
  );
  const normalizedDebugBoxes = normalizeBoundingBoxes(debugBoxes, frameW, frameH, 'debug');
  const normalizedMegaBoxes = resolveMegadetectorBoxes(megadetectorVerify, frameW, frameH);
  const matchedMegaIndexes = new Set();
  const adjustedDebugBoxes = normalizedDebugBoxes.map((box) => {
    let bestIoU = 0;
    let bestIndex = -1;
    normalizedMegaBoxes.forEach((megaBox, index) => {
      const iou = calculateIoU(box, megaBox);
      if (iou > bestIoU) {
        bestIoU = iou;
        bestIndex = index;
      }
    });
    if (bestIoU >= 0.85 && bestIndex >= 0) {
      matchedMegaIndexes.add(bestIndex);
      return {
        ...box,
        source: 'megadetector',
        matched: true,
      };
    }
    return box;
  });
  const unmatchedMegaBoxes = normalizedMegaBoxes.filter((_, index) => !matchedMegaIndexes.has(index));
  return [...adjustedDebugBoxes, ...unmatchedMegaBoxes];
};

const formatConfidenceLabel = (value) => {
  if (!isFiniteNumber(value)) {
    return null;
  }
  const normalized = value > 1 ? value / 100 : value;
  return formatPercent(normalized, 1);
};

const formatBoxLabel = (box, fallbackLabel) => {
  const parts = [];
  const trimmedFallback = typeof fallbackLabel === 'string' ? fallbackLabel.trim() : '';
  if (trimmedFallback) {
    parts.push(trimmedFallback);
  }
  if (typeof box?.label === 'string' && box.label.trim()) {
    const trimmedLabel = box.label.trim();
    if (trimmedLabel && trimmedLabel !== trimmedFallback) {
      parts.push(trimmedLabel);
    }
  }
  const confidence = formatConfidenceLabel(box?.confidence);
  if (confidence) {
    parts.push(confidence);
  }
  if (parts.length === 0 && box?.source === 'megadetector') {
    parts.push('MegaDetector');
  }
  if (parts.length === 0 && typeof fallbackLabel === 'string' && fallbackLabel.trim()) {
    parts.push(fallbackLabel.trim());
  }
  return parts.length > 0 ? parts.join(' ') : null;
};

const DebugBoundingImage = ({ src, alt, boxes, defaultLabel }) => {
  const hasBoxes = Array.isArray(boxes) && boxes.length > 0;
  const containerRef = useRef(null);
  const labelRefs = useRef([]);
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });
  const [labelPositions, setLabelPositions] = useState([]);

  const labeledBoxes = useMemo(() => {
    if (!hasBoxes) {
      return [];
    }
    return boxes.map((box) => {
      const label = formatBoxLabel(box, defaultLabel);
      const speciesLabel = typeof box?.label === 'string' && box.label.trim()
        ? box.label.trim()
        : defaultLabel;
      const borderColor = box.source === 'megadetector'
        ? 'rgba(56, 189, 248, 0.95)'
        : getSpeciesColor(speciesLabel);
      return {
        box,
        label,
        borderColor,
      };
    });
  }, [boxes, defaultLabel, hasBoxes]);

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return undefined;
    }
    const updateSize = () => {
      setContainerSize({
        width: container.clientWidth,
        height: container.clientHeight,
      });
    };
    updateSize();
    const observer = new ResizeObserver(updateSize);
    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  useLayoutEffect(() => {
    if (!hasBoxes || !containerSize.width || !containerSize.height) {
      if (labelPositions.length) {
        setLabelPositions([]);
      }
      return;
    }

    const margin = 4;
    const boxRects = labeledBoxes.map(({ box }) => ({
      left: (box.left / 100) * containerSize.width,
      top: (box.top / 100) * containerSize.height,
      width: (box.width / 100) * containerSize.width,
      height: (box.height / 100) * containerSize.height,
    }));

    const overlaps = (rect, other) =>
      rect.left < other.left + other.width
      && rect.left + rect.width > other.left
      && rect.top < other.top + other.height
      && rect.top + rect.height > other.top;
    const hasOverlap = (rect, others) => {
      for (const other of others) {
        if (overlaps(rect, other)) {
          return true;
        }
      }
      return false;
    };

    const placedLabels = [];
    let overflowTop = 0;

    const nextPositions = labeledBoxes.map((entry, index) => {
      if (!entry.label) {
        return null;
      }
      const labelEl = labelRefs.current[index];
      const labelWidth = labelEl?.offsetWidth || Math.max(48, entry.label.length * 6.5);
      const labelHeight = labelEl?.offsetHeight || 18;
      const boxRect = boxRects[index];

      const candidates = [
        { left: boxRect.left, top: boxRect.top - labelHeight - margin },
        { left: boxRect.left + boxRect.width - labelWidth, top: boxRect.top - labelHeight - margin },
        { left: boxRect.left, top: boxRect.top + boxRect.height + margin },
        {
          left: boxRect.left + boxRect.width - labelWidth,
          top: boxRect.top + boxRect.height + margin,
        },
        { left: boxRect.left + boxRect.width + margin, top: boxRect.top },
        {
          left: boxRect.left + boxRect.width + margin,
          top: boxRect.top + boxRect.height - labelHeight,
        },
        { left: boxRect.left - labelWidth - margin, top: boxRect.top },
        {
          left: boxRect.left - labelWidth - margin,
          top: boxRect.top + boxRect.height - labelHeight,
        },
      ];

      const isClear = (rect) => !hasOverlap(rect, boxRects) && !hasOverlap(rect, placedLabels);

      let chosen = null;
      for (const candidate of candidates) {
        const rect = {
          ...candidate,
          width: labelWidth,
          height: labelHeight,
        };
        if (isClear(rect)) {
          chosen = rect;
          break;
        }
      }

      if (!chosen) {
        let fallbackRect = {
          left: containerSize.width + margin,
          top: overflowTop,
          width: labelWidth,
          height: labelHeight,
        };
        while (hasOverlap(fallbackRect, placedLabels)) {
          fallbackRect = {
            ...fallbackRect,
            top: fallbackRect.top + labelHeight + margin,
          };
        }
        chosen = fallbackRect;
        overflowTop = chosen.top + labelHeight + margin;
      }

      placedLabels.push(chosen);
      return {
        left: chosen.left - boxRect.left,
        top: chosen.top - boxRect.top,
      };
    });

    const positionKey = JSON.stringify(labelPositions);
    const nextKey = JSON.stringify(nextPositions);
    if (positionKey !== nextKey) {
      setLabelPositions(nextPositions);
    }
  }, [containerSize.height, containerSize.width, hasBoxes, labeledBoxes, labelPositions]);

  return (
    <div
      ref={containerRef}
      className={`debugBoundingImage${hasBoxes ? ' debugBoundingImage--boxed' : ''}`}
    >
      <img
        src={src}
        alt={alt}
        className="debugBoundingImage__img"
        loading="lazy"
        decoding="async"
      />
      {hasBoxes && (
        <div className="debugBoundingImage__boxes">
          {labeledBoxes.map(({ box, borderColor, label }, index) => {
            const position = labelPositions[index];
            return (
              <div
                key={`${box.left}-${box.top}-${box.width}-${box.height}-${index}`}
                className={`debugBoundingImage__box${
                  box.source === 'megadetector' ? ' debugBoundingImage__box--megadetector' : ''
                }`}
                style={{
                  left: `${box.left}%`,
                  top: `${box.top}%`,
                  width: `${box.width}%`,
                  height: `${box.height}%`,
                  '--bbox-color': borderColor,
                }}
              >
                {label && (
                  <span
                    ref={(node) => {
                      labelRefs.current[index] = node;
                    }}
                    className="debugBoundingImage__label"
                    style={position ? { left: `${position.left}px`, top: `${position.top}px` } : undefined}
                  >
                    {label}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const resolveMegadetectorVerify = (...sources) => {
  for (const source of sources) {
    const normalized = normalizeMegadetectorVerify(source);
    if (normalized) {
      return normalized;
    }
  }
  return null;
};

const normalizeNumericValue = (value) => {
  if (typeof value === 'number' && !Number.isNaN(value)) {
    return value;
  }
  if (typeof value === 'string') {
    const parsed = Number(value);
    if (!Number.isNaN(parsed)) {
      return parsed;
    }
  }
  return null;
};

const normalizeBooleanValue = (value) => {
  if (typeof value === 'boolean') {
    return value;
  }
  if (typeof value === 'number') {
    if (value === 1) {
      return true;
    }
    if (value === 0) {
      return false;
    }
  }
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (normalized === 'true') {
      return true;
    }
    if (normalized === 'false') {
      return false;
    }
  }
  return null;
};

const normalizeClassifierPrediction = (prediction) => {
  if (!prediction || typeof prediction !== 'object') {
    return null;
  }

  const normalizedPrediction = {
    rank: normalizeNumericValue(prediction.rank),
    tid: normalizeNumericValue(prediction.tid),
    backend: typeof prediction.backend === 'string' && prediction.backend.trim()
      ? prediction.backend.trim()
      : null,
    prob: normalizeNumericValue(prediction.prob),
    confidence: normalizeNumericValue(prediction.confidence ?? prediction.prob),
    passed: normalizeBooleanValue(prediction.passed),
    threshold: normalizeNumericValue(prediction.threshold),
  };

  const hasPredictionValues = Object.values(normalizedPrediction)
    .some((value) => value !== null && value !== undefined);

  return hasPredictionValues ? normalizedPrediction : null;
};

const normalizeClassifierPredictions = (predictions) => {
  if (!Array.isArray(predictions)) {
    return null;
  }

  const normalizedPredictions = predictions
    .map((prediction) => normalizeClassifierPrediction(prediction))
    .filter(Boolean);

  return normalizedPredictions.length > 0 ? normalizedPredictions : null;
};

const normalizeTriggerVerify = (rawVerify) => {
  if (!rawVerify || typeof rawVerify !== 'object') {
    return null;
  }

  const classifierPredictions = normalizeClassifierPredictions(rawVerify.classifier_predictions);

  const normalizedVerify = {
    classifier_prediction_rank: normalizeNumericValue(rawVerify.classifier_prediction_rank),
    classifier_predictions: classifierPredictions || [],
  };

  const hasVerifyValues = normalizedVerify.classifier_prediction_rank !== null
    || normalizedVerify.classifier_predictions.length > 0;

  if (!hasVerifyValues) {
    return null;
  }

  return {
    ...normalizedVerify,
    classifier_predictions: normalizedVerify.classifier_predictions.length > 0
      ? normalizedVerify.classifier_predictions
      : null,
  };
};

const resolveEntryTourClassifierPredictions = (entry, rawTrigger) => {
  const parentLevel = normalizeClassifierPredictions(entry?.meta?.parentDoc?.tour_classifier_predictions);
  if (parentLevel) {
    return parentLevel;
  }

  const speciesLevel = normalizeClassifierPredictions(entry?.meta?.speciesDoc?.tour_classifier_predictions);
  if (speciesLevel) {
    return speciesLevel;
  }

  const triggerLevel = normalizeClassifierPredictions(rawTrigger?.tour_classifier_predictions);
  if (triggerLevel) {
    return triggerLevel;
  }

  const verifyLevel = normalizeClassifierPredictions(rawTrigger?.verify?.tour_classifier_predictions);
  if (verifyLevel) {
    return verifyLevel;
  }

  return null;
};

const normalizeTrigger = (rawTrigger) => {
  if (!rawTrigger || typeof rawTrigger !== 'object') {
    return null;
  }

  const normalizedTier = typeof rawTrigger.tier === 'string' ? rawTrigger.tier.trim() : '';
  const verify = normalizeTriggerVerify(rawTrigger.verify);
  const thresholdData =
    rawTrigger.thresholds && typeof rawTrigger.thresholds === 'object'
      ? {
        min_net_dist: normalizeNumericValue(rawTrigger.thresholds.min_net_dist),
        confirm_hits: normalizeNumericValue(rawTrigger.thresholds.confirm_hits),
        min_persist_hits: normalizeNumericValue(rawTrigger.thresholds.min_persist_hits),
      }
      : null;

  const trigger = {
    tier: normalizedTier || null,
    preset: normalizePresetValue(rawTrigger.preset ?? rawTrigger.preset_id),
    preset_id: normalizePresetValue(rawTrigger.preset_id),
    net_dist: normalizeNumericValue(rawTrigger.net_dist),
    hits: normalizeNumericValue(rawTrigger.hits),
    cons_hits: normalizeNumericValue(rawTrigger.cons_hits),
    persist_hits: normalizeNumericValue(rawTrigger.persist_hits),
    area_ema: normalizeNumericValue(rawTrigger.area_ema),
    speed_ema: normalizeNumericValue(rawTrigger.speed_ema),
    thresholds: thresholdData,
    verify,
  };

  const hasThresholds = thresholdData
    ? Object.values(thresholdData).some((value) => value !== null)
    : false;
  const hasPrimaryFields = ['tier', 'preset', 'preset_id', 'net_dist', 'hits', 'cons_hits', 'persist_hits', 'area_ema', 'speed_ema']
    .map((key) => trigger[key])
    .some((value) => value !== null && value !== undefined);
  const hasVerify = Boolean(verify);

  if (!hasThresholds && !hasPrimaryFields && !hasVerify) {
    return null;
  }

  return {
    ...trigger,
    thresholds: hasThresholds ? thresholdData : null,
    verify,
  };
};

const formatPresetValue = (value) => {
  const normalized = normalizePresetValue(value);
  if (normalized === null || normalized === undefined) {
    return '—';
  }
  if (typeof normalized === 'number') {
    return normalized.toLocaleString(undefined, { maximumFractionDigits: 0 });
  }
  return String(normalized);
};

const buildDebugTrigger = (rawTrigger, mlConfidence = null) => {
  if (!rawTrigger || typeof rawTrigger !== 'object') {
    return null;
  }

  const align = rawTrigger.align && typeof rawTrigger.align === 'object'
    ? {
      dy_proc: normalizeNumericValue(rawTrigger.align.dy_proc),
      dx_proc: normalizeNumericValue(rawTrigger.align.dx_proc),
    }
    : null;

  const trigger = {
    preset: normalizePresetValue(rawTrigger.preset ?? rawTrigger.preset_id),
    ml_confidence: normalizeNumericValue(mlConfidence),
    ml_backend: typeof rawTrigger.ml_backend === 'string' && rawTrigger.ml_backend.trim()
      ? rawTrigger.ml_backend.trim()
      : null,
    ml_prob: normalizeNumericValue(rawTrigger.ml_prob),
    prediction_confidence: normalizeNumericValue(rawTrigger.prediction_confidence),
    ml_threshold: normalizeNumericValue(rawTrigger.ml_threshold),
    ml_passed: normalizeBooleanValue(rawTrigger.ml_passed),
    blob_count: normalizeNumericValue(rawTrigger.blob_count),
    area_th: normalizeNumericValue(rawTrigger.area_th),
    max_blob_small: normalizeNumericValue(rawTrigger.max_blob_small),
    dwell_n: normalizeNumericValue(rawTrigger.dwell_n),
    align,
    motionmask_overlap: normalizeNumericValue(rawTrigger.motionmask_overlap),
    motion_area_seen: normalizeNumericValue(rawTrigger.motion_area_seen),
    fg_frac_small: normalizeNumericValue(rawTrigger.fg_frac_small),
    net_move_seen_px: normalizeNumericValue(rawTrigger.net_move_seen_px),
    green_ratio: normalizeNumericValue(rawTrigger.green_ratio),
    verify: normalizeTriggerVerify(rawTrigger.verify),
    classifier_predictions: normalizeClassifierPredictions(
      rawTrigger.classifier_predictions || rawTrigger.verify?.classifier_predictions,
    ),
    tour_classifier_predictions: normalizeClassifierPredictions(
      rawTrigger.tour_classifier_predictions || rawTrigger.verify?.tour_classifier_predictions,
    ),
  };

  const cleaned = Object.entries(trigger).reduce((acc, [key, value]) => {
    if (value === null || value === undefined) {
      return acc;
    }
    if (key === 'align' && value && typeof value === 'object') {
      const alignValues = Object.entries(value).reduce((alignAcc, [alignKey, alignValue]) => {
        if (alignValue === null || alignValue === undefined) {
          return alignAcc;
        }
        return { ...alignAcc, [alignKey]: alignValue };
      }, {});
      if (Object.keys(alignValues).length === 0) {
        return acc;
      }
      return { ...acc, align: alignValues };
    }
    return { ...acc, [key]: value };
  }, {});

  if (Object.keys(cleaned).length === 0) {
    return null;
  }

  return cleaned;
};

const formatDebugValue = (value) => {
  if (value === null || value === undefined) {
    return '—';
  }

  const normalizedDate = normalizeDate(value);
  if (normalizedDate instanceof Date && !Number.isNaN(normalizedDate.getTime())) {
    return normalizedDate.toISOString();
  }

  if (typeof value === 'number') {
    return Number.isNaN(value) ? '—' : value.toLocaleString();
  }

  if (typeof value === 'boolean') {
    return value ? 'true' : 'false';
  }

  if (typeof value === 'string') {
    return value;
  }

  try {
    return JSON.stringify(value, null, 2);
  } catch (error) {
    return String(value);
  }
};

const isDebugBlockValue = (value) => {
  if (value === null || value === undefined) {
    return false;
  }

  const normalizedDate = normalizeDate(value);
  if (normalizedDate instanceof Date && !Number.isNaN(normalizedDate.getTime())) {
    return false;
  }

  return typeof value === 'object';
};

const ManagedMotionTrackPreview = ({ videoSrc, posterSrc, ariaLabel }) => {
  const containerRef = useRef(null);
  const videoRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const [activeSrc, setActiveSrc] = useState(null);

  useEffect(() => {
    if (!videoSrc) {
      setIsVisible(false);
      return () => {};
    }

    if (typeof window === 'undefined' || typeof IntersectionObserver === 'undefined') {
      setIsVisible(true);
      return () => {};
    }

    const node = containerRef.current;
    if (!node) {
      setIsVisible(false);
      return () => {};
    }

    const observer = new IntersectionObserver(
      ([entry]) => setIsVisible(entry.isIntersecting),
      { threshold: 0.25, rootMargin: '120px' },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [videoSrc]);

  useEffect(() => {
    setActiveSrc(isVisible ? videoSrc : null);
  }, [isVisible, videoSrc]);

  useEffect(() => {
    const videoElement = videoRef.current;
    if (!videoElement) {
      return;
    }

    if (!activeSrc) {
      videoElement.pause();
      if (videoElement.getAttribute('src')) {
        videoElement.removeAttribute('src');
        videoElement.load();
      }
      return;
    }

    videoElement.defaultPlaybackRate = MOTION_TRACK_PLAYBACK_RATE;
    videoElement.playbackRate = MOTION_TRACK_PLAYBACK_RATE;
    videoElement.load();
    const playPromise = videoElement.play();
    if (playPromise && typeof playPromise.catch === 'function') {
      playPromise.catch(() => {});
    }
  }, [activeSrc]);

  return (
    <div className="sightingCard__mediaPreview" ref={containerRef}>
      <video
        ref={videoRef}
        src={activeSrc || undefined}
        poster={posterSrc || undefined}
        aria-label={ariaLabel}
        muted
        loop
        playsInline
        autoPlay={Boolean(activeSrc)}
        preload={activeSrc ? 'metadata' : 'none'}
        onLoadedMetadata={(event) => {
          event.currentTarget.defaultPlaybackRate = MOTION_TRACK_PLAYBACK_RATE;
          event.currentTarget.playbackRate = MOTION_TRACK_PLAYBACK_RATE;
        }}
      />
    </div>
  );
};

const buildMotionTrackSightingEntry = (docSnap) => {
  const parentDoc = { id: docSnap.id, ...docSnap.data() };
  const tally = parentDoc?.entityTally && typeof parentDoc.entityTally === 'object'
    ? parentDoc.entityTally
    : {};
  const fallbackSpecies = Object.keys(tally).find((value) => value && value.trim());
  const species = [parentDoc.primarySpecies, parentDoc.species, fallbackSpecies, 'motion']
    .find((value) => typeof value === 'string' && value.trim())
    .trim();
  const speciesDocId = species.toLowerCase().replace(/\s+/g, '-');
  const count = typeof tally[species] === 'number' ? tally[species] : 1;
  const speciesDoc = {
    id: speciesDocId,
    species,
    count,
    createdAt: parentDoc.createdAt,
    updatedAt: parentDoc.updatedAt,
    clientId: parentDoc.clientId,
    cameraId: parentDoc.cameraId,
  };
  const entry = buildHighlightEntry({
    category: 'sighting',
    speciesDoc,
    parentDoc,
  });

  return {
    ...entry,
    id: `${entry.id}::${speciesDocId}`,
    trigger: normalizeTrigger(parentDoc?.trigger),
    meta: {
      parentPath: docSnap.ref.path,
      speciesDocPath: `${docSnap.ref.path}/perSpecies/${speciesDocId}`,
      parentDoc,
      speciesDoc,
    },
  };
};

export default function Sightings() {
  const [sightings, setSightings] = useState([]);
  const [locationLabelMap, setLocationLabelMap] = useState({});
  const [configuredLocationOptions, setConfiguredLocationOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const isMountedRef = useRef(true);
  const sightingsRef = useRef([]);
  const [activeSighting, setActiveSighting] = useState(null);
  const [selectedSpecies, setSelectedSpecies] = useState([]);
  const [speciesFilterMode, setSpeciesFilterMode] = useState('exclude');
  const [isSpeciesMenuOpen, setIsSpeciesMenuOpen] = useState(false);
  const [locationFilter, setLocationFilter] = useState('all');
  const [mediaTypeFilter, setMediaTypeFilter] = useState('all');
  const [modalViewMode, setModalViewMode] = useState('standard');
  const [isHdEnabled, setIsHdEnabled] = useState(false);
  const [isFullClipEnabled, setIsFullClipEnabled] = useState(false);
  const [isDebugViewEnabled, setIsDebugViewEnabled] = useState(false);
  const [isAnimalBoxesEnabled, setIsAnimalBoxesEnabled] = useState(false);
  const [isMotionMaskEnabled, setIsMotionMaskEnabled] = useState(true);
  const paginationCursorsRef = useRef([]);
  const [hasMore, setHasMore] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [paginationLoading, setPaginationLoading] = useState(false);
  const [selectedSightings, setSelectedSightings] = useState(() => new Set());
  const [sendStatusMap, setSendStatusMap] = useState({});
  const sendingEntryIdsRef = useRef(new Set());
  const [facebookConnection, setFacebookConnection] = useState({
    loading: true,
    connected: false,
    selectedPageId: '',
    pages: [],
    error: '',
  });
  const [facebookPublishTarget, setFacebookPublishTarget] = useState(null);
  const [deleteStatusMap, setDeleteStatusMap] = useState({});
  const [downloadStatusMap, setDownloadStatusMap] = useState({});
  const [highlightStatusMap, setHighlightStatusMap] = useState({});
  const [editTarget, setEditTarget] = useState(null);
  const [editMode, setEditMode] = useState('animal');
  const [editSpeciesInput, setEditSpeciesInput] = useState('');
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState('');
  const [editFeedback, setEditFeedback] = useState({ type: '', text: '' });
  const [newSightingIds, setNewSightingIds] = useState(() => new Set());
  const role = useAuthStore((state) => state.role);
  const locationIds = useAuthStore((state) => state.locationIds);
  const isAccessLoading = useAuthStore((state) => state.isAccessLoading);
  const accessError = useAuthStore((state) => state.accessError);
  const user = useAuthStore((state) => state.user);
  const speciesMenuRef = useRef(null);
  const editSpeciesInputRef = useRef(null);
  const pendingRefreshScrollRef = useRef(false);
  const defaultUnknownFilterAppliedRef = useRef(false);

  const allowedLocationSet = useMemo(() => buildLocationSet(locationIds), [locationIds]);
  const isAdmin = role === 'admin';
  const accessReady = !isAccessLoading;
  const noAssignedLocations = accessReady && !isAdmin && allowedLocationSet.size === 0;

  usePageTitle('Sightings');

  useEffect(() => {
    if (!editTarget || editMode !== 'animal' || editSaving) {
      return;
    }

    const input = editSpeciesInputRef.current;
    if (input) {
      input.focus();
      input.select();
    }
  }, [editMode, editSaving, editTarget]);

  const actorName = useMemo(() => {
    if (!user) {
      return 'Admin';
    }

    const candidates = [user.displayName, user.email, user.phoneNumber];
    const preferred = candidates.find((value) => typeof value === 'string' && value.trim().length > 0);

    if (preferred) {
      return preferred.trim();
    }

    return typeof user.uid === 'string' && user.uid.length > 0 ? user.uid : 'Admin';
  }, [user]);

  const fetchMotionTrackSightingsPage = useCallback(async (startCursor = null) => {
    let queryCursor = startCursor;
    const collectedEntries = [];

    // Access filtering happens client-side, so keep reading until the visible page is full.
    while (collectedEntries.length < SIGHTINGS_PAGE_SIZE) {
      const constraints = [
        // Only track-linked sightings have this field. A single-field ordered
        // query stays newest-first without a large composite index.
        orderBy('motionTrackCreatedAt', 'desc'),
      ];

      if (queryCursor) {
        constraints.push(startAfter(queryCursor));
      }

      constraints.push(limit(SIGHTINGS_PAGE_SIZE));
      const snapshot = await getDocs(query(collection(db, 'sightings'), ...constraints));
      if (!isMountedRef.current) {
        return { aborted: true, entries: [], nextCursor: startCursor, hasMore: false };
      }

      if (snapshot.empty) {
        return {
          aborted: false,
          entries: collectedEntries,
          nextCursor: queryCursor,
          hasMore: false,
        };
      }

      for (let rawIndex = 0; rawIndex < snapshot.docs.length; rawIndex += 1) {
        const docSnap = snapshot.docs[rawIndex];
        const parentDoc = docSnap.data();
        queryCursor = docSnap;

        if (parentDoc?.deletedAt || !getMotionTrackUrl(parentDoc)) {
          continue;
        }

        const entry = buildMotionTrackSightingEntry(docSnap);
        if (!isAdmin && !allowedLocationSet.has(normalizeLocationId(entry.accessId || entry.locationId))) {
          continue;
        }

        collectedEntries.push(entry);
        if (collectedEntries.length === SIGHTINGS_PAGE_SIZE) {
          return {
            aborted: false,
            entries: collectedEntries,
            nextCursor: docSnap,
            hasMore: rawIndex < snapshot.docs.length - 1
              || snapshot.docs.length === SIGHTINGS_PAGE_SIZE,
          };
        }
      }

      if (snapshot.docs.length < SIGHTINGS_PAGE_SIZE) {
        return {
          aborted: false,
          entries: collectedEntries,
          nextCursor: queryCursor,
          hasMore: false,
        };
      }
    }

    return {
      aborted: false,
      entries: collectedEntries,
      nextCursor: queryCursor,
      hasMore: false,
    };
  }, [allowedLocationSet, isAdmin]);

  const fetchSightingsPage = useCallback(async (startCursor = null, mediaFilter = 'all') => {
    if (mediaFilter === 'motion-track') {
      return fetchMotionTrackSightingsPage(startCursor);
    }

    let queryCursor = startCursor;
    const collectedEntries = [];
    const seenEntryIds = new Set();

    // Keep pulling batches until we have a full UI page of usable sightings.
    while (collectedEntries.length < SIGHTINGS_PAGE_SIZE) {
      const constraints = [orderBy('createdAt', 'desc')];

      if (queryCursor) {
        constraints.push(startAfter(queryCursor));
      }

      constraints.push(limit(SIGHTINGS_PAGE_SIZE));

      const sightingsQuery = query(collectionGroup(db, 'perSpecies'), ...constraints);
      const snapshot = await getDocs(sightingsQuery);
      if (!isMountedRef.current) {
        return { aborted: true, entries: [], nextCursor: startCursor, hasMore: false };
      }

      if (snapshot.empty) {
        return {
          aborted: false,
          entries: collectedEntries,
          nextCursor: queryCursor,
          hasMore: false,
        };
      }

      const parentRefMap = new Map();
      snapshot.docs.forEach((docSnap) => {
        const parentRef = docSnap.ref.parent.parent;
        if (parentRef && !parentRefMap.has(parentRef.path)) {
          parentRefMap.set(parentRef.path, parentRef);
        }
      });

      const parentSnaps = await Promise.all(
        Array.from(parentRefMap.values()).map((ref) => getDoc(ref)),
      );
      if (!isMountedRef.current) {
        return { aborted: true, entries: [], nextCursor: startCursor, hasMore: false };
      }

      const parentDataMap = new Map();
      parentSnaps.forEach((snap) => {
        if (!snap.exists()) return;
        const data = snap.data();
        if (data?.deletedAt) return;
        parentDataMap.set(snap.ref.path, { id: snap.id, ...data });
      });

      const visibleItems = snapshot.docs
        .map((docSnap, rawIndex) => {
          const speciesDoc = { id: docSnap.id, ...docSnap.data() };
          if (speciesDoc.deletedAt) return null;

          const parentRef = docSnap.ref.parent.parent;
          if (!parentRef) return null;

          const parentDoc = parentDataMap.get(parentRef.path);
          if (!parentDoc || parentDoc.deletedAt) return null;

          const entry = buildHighlightEntry({
            category: 'sighting',
            speciesDoc,
            parentDoc,
          });

          const normalizedEntry = {
            ...entry,
            id: `${entry.id}::${speciesDoc.id}`,
            trigger: normalizeTrigger(parentDoc?.trigger || speciesDoc?.trigger),
            meta: {
              parentPath: parentRef.path,
              speciesDocPath: docSnap.ref.path,
              parentDoc,
              speciesDoc,
            },
          };

          if (!isAdmin && !allowedLocationSet.has(normalizeLocationId(normalizedEntry.accessId || normalizedEntry.locationId))) {
            return null;
          }

          return {
            entry: normalizedEntry,
            rawIndex,
            cursor: docSnap,
          };
        })
        .filter(Boolean);

      for (const item of visibleItems) {
        if (seenEntryIds.has(item.entry.id)) {
          continue;
        }

        collectedEntries.push(item.entry);
        seenEntryIds.add(item.entry.id);
        queryCursor = item.cursor;

        if (collectedEntries.length === SIGHTINGS_PAGE_SIZE) {
          const hasRemainingRawDocs = item.rawIndex < snapshot.docs.length - 1;
          return {
            aborted: false,
            entries: collectedEntries,
            nextCursor: item.cursor,
            hasMore: hasRemainingRawDocs || snapshot.docs.length === SIGHTINGS_PAGE_SIZE,
          };
        }
      }

      if (snapshot.docs.length < SIGHTINGS_PAGE_SIZE) {
        return {
          aborted: false,
          entries: collectedEntries,
          nextCursor: queryCursor,
          hasMore: false,
        };
      }

      queryCursor = snapshot.docs[snapshot.docs.length - 1] || queryCursor;
    }

    return {
      aborted: false,
      entries: collectedEntries,
      nextCursor: queryCursor,
      hasMore: false,
    };
  }, [allowedLocationSet, fetchMotionTrackSightingsPage, isAdmin]);

  const ensurePageCursor = useCallback(async (pageIndex, mediaFilter = 'all') => {
    if (pageIndex <= 0) {
      return { startCursor: null, resolvedPageIndex: 0 };
    }

    const existingCursor = paginationCursorsRef.current[pageIndex - 1] || null;
    if (existingCursor) {
      return { startCursor: existingCursor, resolvedPageIndex: pageIndex };
    }

    let rebuildPageIndex = paginationCursorsRef.current.length;
    let rebuildStartCursor = rebuildPageIndex > 0
      ? paginationCursorsRef.current[rebuildPageIndex - 1] || null
      : null;

    while (rebuildPageIndex < pageIndex) {
      const rebuiltPage = await fetchSightingsPage(rebuildStartCursor, mediaFilter);
      if (rebuiltPage.aborted) {
        return { aborted: true };
      }

      paginationCursorsRef.current = [
        ...paginationCursorsRef.current.slice(0, rebuildPageIndex),
        rebuiltPage.nextCursor || rebuildStartCursor,
      ];

      if (!rebuiltPage.hasMore) {
        return {
          startCursor: rebuildPageIndex > 0
            ? paginationCursorsRef.current[rebuildPageIndex - 1] || null
            : null,
          resolvedPageIndex: rebuildPageIndex,
        };
      }

      rebuildStartCursor = rebuiltPage.nextCursor;
      rebuildPageIndex += 1;
    }

    return {
      startCursor: paginationCursorsRef.current[pageIndex - 1] || null,
      resolvedPageIndex: pageIndex,
    };
  }, [fetchSightingsPage]);

  const loadSightings = useCallback(async (options = {}) => {
    const {
      pageIndex = 0,
      highlightNew = false,
      mediaFilter = mediaTypeFilter,
    } = options;

    if (!accessReady) {
      return;
    }

    if (!isAdmin && allowedLocationSet.size === 0) {
      setSightings([]);
      setError('');
      paginationCursorsRef.current = [];
      setHasMore(false);
      setLoading(false);
      setPaginationLoading(false);
      setCurrentPage(0);
      writeStoredSightingsPageIndex(0);
      return;
    }

    const requestedPageIndex = sanitizePageIndex(pageIndex);
    const setBusy = requestedPageIndex === 0 ? setLoading : setPaginationLoading;
    setBusy(true);

    let resolvedPageIndex = requestedPageIndex;
    let startCursor = null;
    let isFirstPage = requestedPageIndex === 0;

    try {
      if (resolvedPageIndex > 0) {
        const cursorState = await ensurePageCursor(resolvedPageIndex, mediaFilter);
        if (cursorState?.aborted) {
          return;
        }
        resolvedPageIndex = cursorState?.resolvedPageIndex ?? resolvedPageIndex;
        startCursor = cursorState?.startCursor || null;
      }

      isFirstPage = resolvedPageIndex === 0;

      if (isFirstPage) {
        setError('');
        setHasMore(false);
        paginationCursorsRef.current = [];
      }

      if (!highlightNew) {
        setNewSightingIds(new Set());
      }

      const pageData = await fetchSightingsPage(startCursor, mediaFilter);
      if (pageData.aborted || !isMountedRef.current) {
        return;
      }

      if (pageData.entries.length === 0 && isFirstPage) {
        setSightings([]);
        if (highlightNew) {
          setNewSightingIds(new Set());
        }
        setHasMore(false);
        setCurrentPage(0);
        writeStoredSightingsPageIndex(0);
        return;
      }

      if (pageData.entries.length === 0 && resolvedPageIndex > 0) {
        setHasMore(false);
        return;
      }

      setError('');
      setSightings(pageData.entries);
      if (highlightNew) {
        const previousIds = new Set(sightingsRef.current.map((entry) => entry.id));
        const nextIds = pageData.entries
          .filter((entry) => !previousIds.has(entry.id))
          .map((entry) => entry.id);
        setNewSightingIds(new Set(nextIds));
      }

      paginationCursorsRef.current = [
        ...paginationCursorsRef.current.slice(0, resolvedPageIndex),
        pageData.nextCursor || startCursor,
      ];
      setCurrentPage(resolvedPageIndex);
      setHasMore(pageData.hasMore);
      writeStoredSightingsPageIndex(resolvedPageIndex);
    } catch (err) {
      console.error('Failed to fetch sightings', err);
      if (isMountedRef.current) {
        if (isFirstPage) {
          setError('Unable to load sightings');
          setSightings([]);
        } else {
          setError('Unable to load sightings page');
        }
      }
    } finally {
      if (isMountedRef.current) {
        setBusy(false);
      }
    }
  }, [accessReady, ensurePageCursor, fetchSightingsPage, isAdmin, allowedLocationSet, mediaTypeFilter]);

  useEffect(() => {
    sightingsRef.current = sightings;
  }, [sightings]);

  useEffect(() => {
    writeStoredSightingsPageIndex(currentPage);
  }, [currentPage]);

  useEffect(() => {
    isMountedRef.current = true;
    loadSightings({ pageIndex: readStoredSightingsPageIndex() });

    return () => {
      isMountedRef.current = false;
    };
  }, [loadSightings]);

  useEffect(() => {
    if (!user) {
      setFacebookConnection({
        loading: false,
        connected: false,
        selectedPageId: '',
        pages: [],
        error: '',
      });
      return undefined;
    }

    let didCancel = false;
    setFacebookConnection((current) => ({
      ...current,
      loading: true,
      error: '',
    }));

    loadFacebookPageConnection()
      .then((connection) => {
        if (didCancel) return;
        setFacebookConnection({
          loading: false,
          connected: Boolean(connection?.connected),
          selectedPageId: connection?.selectedPageId || '',
          pages: Array.isArray(connection?.pages) ? connection.pages : [],
          error: '',
        });
      })
      .catch((connectionError) => {
        if (didCancel) return;
        setFacebookConnection({
          loading: false,
          connected: false,
          selectedPageId: '',
          pages: [],
          error: getFacebookConnectionErrorMessage(connectionError),
        });
      });

    return () => {
      didCancel = true;
    };
  }, [user]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentPage]);

  const sightingLocationIds = useMemo(() => Array.from(new Set(
    sightings
      .map((entry) => (typeof entry.locationId === 'string' ? entry.locationId.trim() : ''))
      .filter((value) => value.length > 0),
  )), [sightings]);

  useEffect(() => {
    if (!accessReady) {
      return undefined;
    }

    if (!isAdmin && allowedLocationSet.size === 0) {
      setConfiguredLocationOptions([]);
      return undefined;
    }

    let didCancel = false;

    const fetchDocumentsByIds = async (collectionName, ids) => {
      const snapshots = await Promise.all(
        chunkItems(ids, LOOKUP_BATCH_SIZE).map((idBatch) => getDocs(
          query(
            collection(db, collectionName),
            where(documentId(), 'in', idBatch),
          ),
        )),
      );

      return snapshots.flatMap((snapshot) => snapshot.docs);
    };

    const loadConfiguredLocations = async () => {
      try {
        let clientDocs = [];

        if (isAdmin) {
          const clientSnapshot = await getDocs(collection(db, 'clients'));
          clientDocs = clientSnapshot.docs;
        } else {
          const assignedIds = Array.from(allowedLocationSet);
          const [directClientDocs, cameraDocs] = await Promise.all([
            fetchDocumentsByIds('clients', assignedIds),
            fetchDocumentsByIds('cameras', assignedIds),
          ]);
          const directClientIdSet = new Set(directClientDocs.map((docSnap) => docSnap.id));
          const derivedClientIds = Array.from(new Set(
            cameraDocs
              .map((docSnap) => hydrateCameraDraft({ ...docSnap.data(), id: docSnap.id }).clientId)
              .map(normalizeLocationId)
              .filter((clientId) => clientId && !directClientIdSet.has(clientId)),
          ));
          const derivedClientDocs = await fetchDocumentsByIds('clients', derivedClientIds);
          clientDocs = [...directClientDocs, ...derivedClientDocs];
        }

        if (didCancel || !isMountedRef.current) {
          return;
        }

        const nextOptionMap = new Map();
        clientDocs.forEach((docSnap) => {
          const client = hydrateClientDraft({ ...docSnap.data(), id: docSnap.id });
          const value = normalizeLocationId(docSnap.id);
          if (!value || client.enabled === false) {
            return;
          }

          nextOptionMap.set(value, {
            value,
            label: client.name || docSnap.id,
          });
        });

        const nextOptions = Array.from(nextOptionMap.values()).sort((left, right) => {
          const labelComparison = left.label.localeCompare(right.label, undefined, { sensitivity: 'base' });
          if (labelComparison !== 0) {
            return labelComparison;
          }
          return left.value.localeCompare(right.value, undefined, { sensitivity: 'base' });
        });

        setConfiguredLocationOptions(nextOptions);
        setLocationLabelMap((current) => {
          let hasChanges = false;
          const merged = { ...current };

          nextOptions.forEach(({ value, label }) => {
            if (merged[value] !== label) {
              merged[value] = label;
              hasChanges = true;
            }
          });

          return hasChanges ? merged : current;
        });
      } catch (lookupError) {
        console.error('Failed to load configured locations', lookupError);
      }
    };

    loadConfiguredLocations();

    return () => {
      didCancel = true;
    };
  }, [accessReady, allowedLocationSet, isAdmin]);

  useEffect(() => {
    const missingLocationIds = sightingLocationIds.filter(
      (locationId) => locationLabelMap[locationId] === undefined,
    );

    if (missingLocationIds.length === 0) {
      return undefined;
    }

    let didCancel = false;

    const loadLocationLabels = async () => {
      try {
        const directClientSnapshots = await Promise.all(
          chunkItems(missingLocationIds, LOOKUP_BATCH_SIZE).map((locationIdBatch) => getDocs(
            query(
              collection(db, 'clients'),
              where(documentId(), 'in', locationIdBatch),
            ),
          )),
        );

        if (didCancel || !isMountedRef.current) {
          return;
        }

        const nextLabels = {};
        const unresolvedLocationIds = [];

        directClientSnapshots.forEach((snapshot) => {
          snapshot.docs.forEach((docSnap) => {
            const client = hydrateClientDraft({ ...docSnap.data(), id: docSnap.id });
            nextLabels[docSnap.id] = client.name || docSnap.id;
          });
        });

        missingLocationIds.forEach((locationId) => {
          if (nextLabels[locationId] === undefined) {
            unresolvedLocationIds.push(locationId);
          }
        });

        if (unresolvedLocationIds.length === 0) {
          setLocationLabelMap((current) => {
            let hasChanges = false;
            const merged = { ...current };

            Object.entries(nextLabels).forEach(([locationId, label]) => {
              if (merged[locationId] !== label) {
                merged[locationId] = label;
                hasChanges = true;
              }
            });

            return hasChanges ? merged : current;
          });
          return;
        }

        const cameraSnapshots = await Promise.all(
          chunkItems(unresolvedLocationIds, LOOKUP_BATCH_SIZE).map((cameraIdBatch) => getDocs(
            query(
              collection(db, 'cameras'),
              where(documentId(), 'in', cameraIdBatch),
            ),
          )),
        );

        if (didCancel || !isMountedRef.current) {
          return;
        }

        const cameraClientMap = new Map();
        const foundCameraIds = new Set();

        cameraSnapshots.forEach((snapshot) => {
          snapshot.docs.forEach((docSnap) => {
            foundCameraIds.add(docSnap.id);
            const camera = hydrateCameraDraft({ ...docSnap.data(), id: docSnap.id });
            cameraClientMap.set(docSnap.id, camera.clientId || '');
          });
        });

        const clientIds = Array.from(new Set(
          Array.from(cameraClientMap.values()).filter(
            (clientId) => typeof clientId === 'string' && clientId.trim().length > 0,
          ),
        ));

        const clientSnapshots = await Promise.all(
          chunkItems(clientIds, LOOKUP_BATCH_SIZE).map((clientIdBatch) => getDocs(
            query(
              collection(db, 'clients'),
              where(documentId(), 'in', clientIdBatch),
            ),
          )),
        );

        if (didCancel || !isMountedRef.current) {
          return;
        }

        const clientNameMap = new Map();
        clientSnapshots.forEach((snapshot) => {
          snapshot.docs.forEach((docSnap) => {
            const client = hydrateClientDraft({ ...docSnap.data(), id: docSnap.id });
            clientNameMap.set(docSnap.id, client.name || docSnap.id);
          });
        });

        unresolvedLocationIds.forEach((cameraId) => {
          if (!foundCameraIds.has(cameraId)) {
            nextLabels[cameraId] = cameraId;
            return;
          }

          const clientId = cameraClientMap.get(cameraId) || '';
          nextLabels[cameraId] = clientNameMap.get(clientId) || clientId || cameraId;
        });

        setLocationLabelMap((current) => {
          let hasChanges = false;
          const merged = { ...current };

          Object.entries(nextLabels).forEach(([cameraId, label]) => {
            if (merged[cameraId] !== label) {
              merged[cameraId] = label;
              hasChanges = true;
            }
          });

          return hasChanges ? merged : current;
        });
      } catch (lookupError) {
        console.error('Failed to load location labels', lookupError);
      }
    };

    loadLocationLabels();

    return () => {
      didCancel = true;
    };
  }, [locationLabelMap, sightingLocationIds]);

  const getLocationLabel = useCallback((locationId) => {
    const normalizedLocationId = typeof locationId === 'string' ? locationId.trim() : '';
    if (!normalizedLocationId) {
      return 'Unknown location';
    }
    return locationLabelMap[normalizedLocationId] || normalizedLocationId;
  }, [locationLabelMap]);

  const availableLocations = useMemo(
    () => [...sightingLocationIds].sort((a, b) => a.localeCompare(b)),
    [sightingLocationIds],
  );

  const availableLocationOptions = useMemo(() => availableLocations
    .map((locationId) => ({
      value: locationId,
      label: getLocationLabel(locationId),
    }))
    .sort((left, right) => {
      const labelComparison = left.label.localeCompare(right.label, undefined, { sensitivity: 'base' });
      if (labelComparison !== 0) {
        return labelComparison;
      }
      return left.value.localeCompare(right.value, undefined, { sensitivity: 'base' });
    }), [availableLocations, getLocationLabel]);

  const locationsWithoutSightings = useMemo(() => {
    const currentPageLocationSet = new Set(sightingLocationIds.map(normalizeLocationId));
    return configuredLocationOptions.filter(
      ({ value }) => !currentPageLocationSet.has(normalizeLocationId(value)),
    );
  }, [configuredLocationOptions, sightingLocationIds]);

  const activeSightingLocationLabel = useMemo(
    () => getLocationLabel(activeSighting?.locationId),
    [activeSighting?.locationId, getLocationLabel],
  );

  const availableSpecies = useMemo(() => {
    const speciesMap = new Map();
    sightings.forEach((entry) => {
      if (typeof entry.species !== 'string') {
        return;
      }
      const trimmed = entry.species.trim();
      if (!trimmed) {
        return;
      }
      const normalized = trimmed.toLowerCase();
      if (!speciesMap.has(normalized)) {
        speciesMap.set(normalized, trimmed);
      }
    });

    return Array.from(speciesMap.entries())
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [sightings]);

  useEffect(() => {
    if (isAdmin || defaultUnknownFilterAppliedRef.current) {
      return;
    }
    if (selectedSpecies.length > 0 || speciesFilterMode !== 'exclude') {
      return;
    }
    const hasUnknown = availableSpecies.some((item) => item.value === 'unknown');
    if (!hasUnknown) {
      return;
    }
    defaultUnknownFilterAppliedRef.current = true;
    setSpeciesFilterMode('exclude');
    setSelectedSpecies(['unknown']);
  }, [availableSpecies, isAdmin, selectedSpecies, speciesFilterMode]);

  useEffect(() => {
    if (locationFilter === 'all') {
      return;
    }
    if (!availableLocations.includes(locationFilter)) {
      setLocationFilter('all');
    }
  }, [availableLocations, locationFilter]);

  useEffect(() => {
    if (!isSpeciesMenuOpen) {
      return undefined;
    }

    const handleClickOutside = (event) => {
      if (speciesMenuRef.current && !speciesMenuRef.current.contains(event.target)) {
        setIsSpeciesMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isSpeciesMenuOpen]);

  useEffect(() => {
    setSelectedSpecies((prev) => {
      if (prev.length === 0) {
        return prev;
      }
      const validValues = new Set(availableSpecies.map((item) => item.value));
      const filtered = prev.filter((value) => validValues.has(value));
      if (filtered.length === prev.length) {
        return prev;
      }
      trackEvent('sightings_species_filter', { species: filtered, reason: 'pruned' });
      return filtered;
    });
  }, [availableSpecies]);

  useEffect(() => {
    setSelectedSightings((prev) => {
      if (prev.size === 0) {
        return prev;
      }
      const validIds = new Set(sightings.map((entry) => entry.id));
      const filtered = new Set([...prev].filter((id) => validIds.has(id)));
      if (filtered.size === prev.size) {
        return prev;
      }
      return filtered;
    });
  }, [sightings]);

  const filteredSightings = useMemo(
    () => sightings.filter((entry) => {
      if (locationFilter !== 'all' && entry.locationId !== locationFilter) {
        return false;
      }

      if (!matchesSightingMediaFilter(entry, mediaTypeFilter)) {
        return false;
      }

      if (selectedSpecies.length > 0) {
        const normalizedSpecies = typeof entry.species === 'string'
          ? entry.species.trim().toLowerCase()
          : '';
        if (speciesFilterMode === 'include') {
          if (!selectedSpecies.includes(normalizedSpecies)) {
            return false;
          }
        } else if (selectedSpecies.includes(normalizedSpecies)) {
          return false;
        }
      }

      return true;
    }),
    [sightings, locationFilter, mediaTypeFilter, selectedSpecies, speciesFilterMode],
  );

  const hasAnySightings = sightings.length > 0;
  const hasSightings = filteredSightings.length > 0;
  const selectedSightingsList = useMemo(
    () => sightings.filter((entry) => selectedSightings.has(entry.id)),
    [sightings, selectedSightings],
  );
  const selectedSightingsCount = selectedSightingsList.length;
  const isAllFilteredSelected = useMemo(
    () => filteredSightings.length > 0
      && filteredSightings.every((entry) => selectedSightings.has(entry.id)),
    [filteredSightings, selectedSightings],
  );
  const hasPendingSelectedDeletes = selectedSightingsList.some(
    (entry) => deleteStatusMap[entry.id]?.state === 'pending',
  );

  const getConfidenceClass = (value) => {
    if (typeof value !== 'number' || Number.isNaN(value)) {
      return 'sightingCard--unknown';
    }
    if (value >= 0.7) {
      return 'sightingCard--high';
    }
    if (value >= 0.5) {
      return 'sightingCard--medium';
    }
    return 'sightingCard--low';
  };

  const handleOpenSighting = useCallback((entry, { startWithFullClip = false } = {}) => {
    const shouldStartWithFullClip = Boolean(
      startWithFullClip
      && entry?.mediaType === 'video'
      && getMotionTrackUrl(entry)
      && pickFirstSource(entry.mediaUrl, entry.videoUrl),
    );
    setActiveSighting(entry);
    setModalViewMode('standard');
    setIsFullClipEnabled(shouldStartWithFullClip);
    trackButton('sighting_open', {
      species: entry?.species,
      mediaType: entry?.mediaType,
      location: entry?.locationId,
    });
  }, []);

  const { clear: clearSightingLink, message: sightingLinkMessage, linkedId: linkedSightingId } = useSightingDeepLink({
    accessReady, isAdmin, allowedLocations: allowedLocationSet, onOpen: handleOpenSighting,
  });

  const handleCloseSighting = useCallback((source = 'dismiss') => {
    clearSightingLink();
    const targetId = activeSighting?.id;
    setActiveSighting(null);
    setModalViewMode('standard');
    setIsFullClipEnabled(false);
    trackButton('sighting_close', {
      source,
      species: activeSighting?.species,
      location: activeSighting?.locationId,
    });

    if (targetId && typeof document !== 'undefined') {
      const escapedId = typeof window !== 'undefined' && window.CSS?.escape
        ? window.CSS.escape(targetId)
        : targetId.replace(/"/g, '\\"');
      window.requestAnimationFrame(() => {
        const target = document.querySelector(`[data-sighting-id="${escapedId}"]`);
        if (target) {
          target.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      });
    }
  }, [activeSighting, clearSightingLink]);

  const handleNavigateSighting = useCallback((direction, source = 'button') => {
    if (!activeSighting || filteredSightings.length === 0) {
      return;
    }
    const currentIndex = filteredSightings.findIndex((entry) => entry.id === activeSighting.id);
    if (currentIndex === -1) {
      return;
    }
    const nextIndex = currentIndex + direction;
    if (nextIndex < 0 || nextIndex >= filteredSightings.length) {
      return;
    }
    trackButton('sighting_navigate', {
      source,
      direction: direction > 0 ? 'next' : 'previous',
      species: activeSighting?.species,
      location: activeSighting?.locationId,
    });
    handleOpenSighting(filteredSightings[nextIndex], { startWithFullClip: false });
  }, [activeSighting, filteredSightings, handleOpenSighting]);

  const handleSetActiveSightingSelection = useCallback((shouldSelect, source = 'keyboard') => {
    if (!activeSighting?.id) {
      return;
    }
    setSelectedSightings((prev) => {
      const next = new Set(prev);
      if (shouldSelect) {
        next.add(activeSighting.id);
      } else {
        next.delete(activeSighting.id);
      }
      return next;
    });
    trackButton('sightings_select_toggle', {
      source,
      selected: shouldSelect,
      sightingId: activeSighting.id,
      species: activeSighting.species,
      location: activeSighting.locationId,
    });
  }, [activeSighting]);

  const handleDebugViewToggle = useCallback((enabled, source = 'filters') => {
    setIsDebugViewEnabled(enabled);
    trackButton('sightings_toggle_debug', { enabled, source });
  }, []);

  const handleMotionMaskToggle = useCallback((enabled, source = 'filters') => {
    setIsMotionMaskEnabled(enabled);
    trackButton('sighting_toggle_motion', {
      enabled,
      source,
      species: activeSighting?.species,
      location: activeSighting?.locationId,
    });
  }, [activeSighting]);

  const handleAnimalBoxesToggle = useCallback((enabled, source = 'filters') => {
    setIsAnimalBoxesEnabled(enabled);
    trackButton('sightings_toggle_boxes', {
      enabled,
      source,
      species: activeSighting?.species,
      location: activeSighting?.locationId,
    });
  }, [activeSighting]);

  const handleSpeciesMenuToggle = useCallback(() => {
    setIsSpeciesMenuOpen((prev) => {
      const nextOpen = !prev;
      trackButton('sightings_species_menu', { open: nextOpen });
      return nextOpen;
    });
  }, []);

  const handleSpeciesToggle = (value) => {
    setSelectedSpecies((prev) => {
      const hasValue = prev.includes(value);
      const nextSelection = hasValue
        ? prev.filter((item) => item !== value)
        : [...prev, value];
      trackEvent('sightings_species_filter', {
        action: hasValue ? 'remove' : 'add',
        value,
        species: nextSelection,
        mode: speciesFilterMode,
      });
      return nextSelection;
    });
  };

  const handleSpeciesClear = () => {
    setSelectedSpecies((prev) => {
      if (prev.length === 0) {
        return prev;
      }
      trackEvent('sightings_species_filter', { action: 'clear', species: [], mode: speciesFilterMode });
      return [];
    });
  };

  const handleSpeciesModeChange = (event) => {
    const nextMode = event.target.value;
    setSpeciesFilterMode(nextMode);
    trackEvent('sightings_species_filter', {
      action: 'mode',
      mode: nextMode,
      species: selectedSpecies,
    });
  };

  const selectedSpeciesSummary = useMemo(() => {
    if (selectedSpecies.length === 0) {
      return 'All species';
    }
    const labelMap = new Map(availableSpecies.map((item) => [item.value, item.label]));
    const formattedSelection = selectedSpecies
      .map((value) => labelMap.get(value) || value);
    if (speciesFilterMode === 'exclude') {
      if (formattedSelection.length <= 2) {
        return `All except ${formattedSelection.join(', ')}`;
      }
      return `All except ${formattedSelection.length} species`;
    }
    if (formattedSelection.length <= 2) {
      return formattedSelection.join(', ');
    }
    return `${formattedSelection.length} selected`;
  }, [selectedSpecies, availableSpecies, speciesFilterMode]);

  useEffect(() => {
    if (!activeSighting) {
      return;
    }
    setIsHdEnabled(false);
  }, [activeSighting]);

  useEffect(() => {
    if (!pendingRefreshScrollRef.current) {
      return;
    }

    if (newSightingIds.size === 0) {
      pendingRefreshScrollRef.current = false;
      return;
    }

    const listToCheck = filteredSightings.length > 0 ? filteredSightings : sightings;
    const oldestNew = [...listToCheck].reverse().find((entry) => newSightingIds.has(entry.id));
    if (oldestNew && typeof document !== 'undefined') {
      const escapedId = typeof window !== 'undefined' && window.CSS?.escape
        ? window.CSS.escape(oldestNew.id)
        : oldestNew.id.replace(/"/g, '\\"');
      window.requestAnimationFrame(() => {
        const target = document.querySelector(`[data-sighting-id="${escapedId}"]`);
        if (target) {
          target.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      });
    }

    pendingRefreshScrollRef.current = false;
  }, [filteredSightings, newSightingIds, sightings]);

  const handleLocationFilterChange = (event) => {
    const nextValue = event.target.value;
    setLocationFilter(nextValue);
    trackEvent('sightings_location_filter', { location: nextValue });
  };

  const handleMediaTypeFilterChange = (event) => {
    const nextValue = event.target.value;
    paginationCursorsRef.current = [];
    setCurrentPage(0);
    writeStoredSightingsPageIndex(0);
    setMediaTypeFilter(nextValue);
    trackEvent('sightings_media_filter', { mediaType: nextValue });
  };

  const hasPreviousPage = currentPage > 0;

  const handleNextPage = useCallback(() => {
    if (loading || paginationLoading || !hasMore) {
      return;
    }
    trackButton('sightings_page_next');
    loadSightings({ pageIndex: currentPage + 1 });
  }, [currentPage, hasMore, loadSightings, loading, paginationLoading]);

  const handlePreviousPage = useCallback(() => {
    if (loading || paginationLoading || !hasPreviousPage) {
      return;
    }
    trackButton('sightings_page_previous');
    loadSightings({ pageIndex: currentPage - 1 });
  }, [currentPage, hasPreviousPage, loadSightings, loading, paginationLoading]);

  const editChange = useMemo(() => {
    if (!editTarget) {
      return null;
    }
    return describeSpeciesChange({ mode: editMode, species: editSpeciesInput });
  }, [editTarget, editMode, editSpeciesInput]);

  const editNotePreview = useMemo(() => {
    if (!editTarget || !editChange) {
      return '';
    }
    return buildCorrectionNote({
      actor: actorName,
      previousSpecies: editTarget.species,
      nextLabel: editChange.label,
      locationId: editTarget.locationId,
      includeTimestamp: false,
    });
  }, [editTarget, editChange, actorName]);

  const handleOpenEditModal = useCallback((entry, source = 'card') => {
    if (!entry) {
      return;
    }

    const normalizedSpecies = typeof entry.species === 'string' ? entry.species.trim() : '';
    const initialMode = normalizedSpecies.toLowerCase() === 'background' ? 'background' : 'animal';

    setEditTarget(entry);
    setEditMode(initialMode);
    setEditSpeciesInput(initialMode === 'animal' ? normalizedSpecies : '');
    setEditError('');
    trackButton('sighting_edit_open', {
      source,
      species: entry.species,
      location: entry.locationId,
    });
  }, []);

  const handleCloseEditModal = useCallback((source = 'dismiss') => {
    if (editSaving) {
      return;
    }
    if (editTarget) {
      trackButton('sighting_edit_close', {
        source,
        species: editTarget.species,
        location: editTarget.locationId,
      });
    }
    setEditTarget(null);
    setEditMode('animal');
    setEditSpeciesInput('');
    setEditError('');
  }, [editSaving, editTarget]);

  const handleEditModeChange = useCallback((event) => {
    const nextMode = event.target.value;
    setEditMode(nextMode);
    if (nextMode === 'background') {
      setEditSpeciesInput('');
    }
    trackEvent('sighting_edit_mode', { mode: nextMode });
  }, []);

  const handleDismissFeedback = useCallback(() => {
    setEditFeedback({ type: '', text: '' });
    trackButton('sighting_edit_feedback_dismiss');
  }, []);

  const syncHighlightStateForParent = useCallback((parentPath, updates) => {
    if (!parentPath || !updates) {
      return;
    }

    const applyUpdates = (entry) => {
      if (!entry || entry?.meta?.parentPath !== parentPath) {
        return entry;
      }

      return {
        ...entry,
        ...updates,
        meta: entry.meta
          ? {
            ...entry.meta,
            parentDoc: {
              ...(entry.meta.parentDoc || {}),
              ...updates,
            },
            speciesDoc: {
              ...(entry.meta.speciesDoc || {}),
              ...updates,
            },
          }
          : entry.meta,
      };
    };

    setSightings((prev) => prev.map(applyUpdates));
    setActiveSighting((prev) => applyUpdates(prev));
    setEditTarget((prev) => applyUpdates(prev));
  }, []);

  const handleSubmitEdit = useCallback(
    async (event) => {
      event.preventDefault();
      if (!editTarget) {
        return;
      }

      if (editMode === 'animal') {
        const trimmed = editSpeciesInput.trim();
        if (!trimmed) {
          setEditError('Please enter a species name.');
          return;
        }
      }

      setEditSaving(true);
      setEditError('');
      trackButton('sighting_edit_save', {
        mode: editMode,
        species: editTarget.species,
        location: editTarget.locationId,
      });

      try {
        const change = describeSpeciesChange({ mode: editMode, species: editSpeciesInput });
        const finalNote = buildCorrectionNote({
          actor: actorName,
          previousSpecies: editTarget.species,
          nextLabel: change.label,
          locationId: editTarget.locationId,
        });

        const authToken =
          typeof user?.getIdToken === 'function' ? await user.getIdToken() : null;

        const result = await applySightingCorrection({
          entry: editTarget,
          mode: editMode,
          nextSpeciesName: editSpeciesInput,
          actor: actorName,
          note: finalNote,
          change,
          relocateMedia: true,
          authToken,
        });

        if (result.mediaRelocationError) {
          trackEvent('sighting_edit_media_error', {
            mode: editMode,
            location: editTarget.locationId,
            error: toAnalyticsError(result.mediaRelocationError),
          });
          setEditFeedback({
            type: 'error',
            text: `Sighting corrected, but media move failed: ${result.mediaRelocationError}`,
          });
        }

        const baseId = typeof editTarget.id === 'string' ? editTarget.id.split('::')[0] : '';
        const nextSpeciesDocId = result.speciesDocId || editTarget.meta?.speciesDoc?.id || '';
        const nextEntryId =
          baseId && nextSpeciesDocId ? `${baseId}::${nextSpeciesDocId}` : editTarget.id;
        const nextSpeciesDocPath = result.speciesDocPath || editTarget.meta?.speciesDocPath || '';

        setSightings((prev) =>
          prev.map((item) => {
            if (item.id !== editTarget.id) {
              return item;
            }

            const nextMeta = {
              ...(item.meta || {}),
              parentDoc: {
                ...(item.meta?.parentDoc || {}),
                ...result.parentDocUpdates,
              },
              speciesDoc: {
                ...(item.meta?.speciesDoc || {}),
                ...(result.speciesDocUpdates || {}),
                id: nextSpeciesDocId || item.meta?.speciesDoc?.id,
              },
              speciesDocPath: nextSpeciesDocPath || item.meta?.speciesDocPath,
            };

            return {
              ...item,
              ...result.parentDocUpdates,
              id: nextEntryId,
              species: result.change.label,
              mediaUrl: result.parentDocUpdates.mediaUrl ?? item.mediaUrl,
              previewUrl: result.parentDocUpdates.previewUrl ?? item.previewUrl,
              videoUrl: result.parentDocUpdates.videoUrl ?? item.videoUrl,
              debugUrl: result.parentDocUpdates.debugUrl ?? item.debugUrl,
              meta: nextMeta,
            };
          }),
        );

        setActiveSighting((prev) => {
          if (!prev || prev.id !== editTarget.id) {
            return prev;
          }

          const nextMeta = prev.meta
            ? {
              ...prev.meta,
              parentDoc: {
                ...(prev.meta.parentDoc || {}),
                ...result.parentDocUpdates,
              },
              speciesDoc: {
                ...(prev.meta.speciesDoc || {}),
                ...(result.speciesDocUpdates || {}),
                id: nextSpeciesDocId || prev.meta?.speciesDoc?.id,
              },
              speciesDocPath: nextSpeciesDocPath || prev.meta?.speciesDocPath,
            }
            : prev.meta;

          return {
            ...prev,
            ...result.parentDocUpdates,
            id: nextEntryId,
            species: result.change.label,
            mediaUrl: result.parentDocUpdates.mediaUrl ?? prev.mediaUrl,
            previewUrl: result.parentDocUpdates.previewUrl ?? prev.previewUrl,
            videoUrl: result.parentDocUpdates.videoUrl ?? prev.videoUrl,
            debugUrl: result.parentDocUpdates.debugUrl ?? prev.debugUrl,
            meta: nextMeta,
          };
        });

        if (nextEntryId !== editTarget.id) {
          setSelectedSightings((prev) => {
            if (!prev.has(editTarget.id)) return prev;
            const next = new Set(prev);
            next.delete(editTarget.id);
            next.add(nextEntryId);
            return next;
          });
        }

        if (!result.mediaRelocationError) {
          setEditFeedback({ type: 'success', text: `Sighting corrected to ${result.change.label}.` });
        }

        trackEvent('sighting_edit_success', {
          mode: editMode,
          previousSpecies: editTarget.species,
          nextSpecies: result.change.label,
          location: editTarget.locationId,
          mediaMoveFailed: Boolean(result.mediaRelocationError),
        });
        setEditTarget(null);
        setEditMode('animal');
        setEditSpeciesInput('');
      } catch (err) {
        console.error('Failed to correct sighting', err);
        trackEvent('sighting_edit_error', {
          mode: editMode,
          location: editTarget.locationId,
          error: toAnalyticsError(err?.message || 'Unable to update sighting.'),
        });
        setEditError(err?.message || 'Unable to update sighting.');
      } finally {
        setEditSaving(false);
      }
    },
    [editTarget, editMode, editSpeciesInput, actorName, user],
  );


  const handleSendToWhatsApp = useCallback(
    async (entry, options = {}) => {
      const { alertStyle } = options;
      const isAlert = alertStyle === 'emoji';

      if (!entry || typeof entry.id !== 'string') {
        return;
      }

      const entryId = entry.id;
      if (sendingEntryIdsRef.current.has(entryId)) {
        return;
      }

      sendingEntryIdsRef.current.add(entryId);
      const updateSendStatus = (status) => {
        setSendStatusMap((prev) => ({
          ...prev,
          [entryId]: status,
        }));
      };

      updateSendStatus({ state: 'pending' });

      let entryForSend = entry;
      let speciesToSend = entry?.species;
      let correctionResult = null;
      let requestedCorrectionSpecies = '';
      let videoClipSelection = DEFAULT_WHATSAPP_CLIP_SELECTION;

      try {
        if (!SEND_WHATSAPP_ENDPOINT) {
          updateSendStatus({
            state: 'error',
            message: 'WhatsApp sending is not configured.',
          });
          return;
        }

        if (!entry.locationId) {
          updateSendStatus({
            state: 'error',
            message: 'Location information is missing for this sighting.',
          });
          return;
        }

        if (typeof window !== 'undefined') {
          const wantsCorrection = window.confirm('Do you want to change the animal before sending?');
          if (wantsCorrection) {
            const manualSpecies = window.prompt(
              'Video/photo of the',
              speciesToSend || '',
            );

            if (typeof manualSpecies === 'string') {
              const normalizedSpecies = manualSpecies.trim();
              requestedCorrectionSpecies = normalizedSpecies;
              speciesToSend = normalizedSpecies || speciesToSend;
            }
          }

          if (isAlert) {
            const spellingReview = reviewAlertSpeciesSpelling(speciesToSend, window);
            if (spellingReview.cancelled) {
              updateSendStatus({ state: 'idle', message: '' });
              return;
            }

            if (spellingReview.species !== speciesToSend) {
              requestedCorrectionSpecies = spellingReview.species;
              speciesToSend = spellingReview.species;
            }
          }

          const mediaSource = pickFirstSource(entryForSend.mediaUrl, entryForSend.videoUrl, entryForSend.previewUrl);
          if (!mediaSource) {
            updateSendStatus({
              state: 'error',
              message: 'No media is available to send for this sighting.',
            });
            return;
          }

          if (entryForSend.mediaType === 'video') {
            const clipChoice = promptForWhatsAppClipSelection();
            if (clipChoice.cancelled) {
              updateSendStatus({ state: 'idle', message: '' });
              return;
            }
            videoClipSelection = clipChoice.clipSelection;
          }
        }

        let mediaSource = pickFirstSource(entryForSend.mediaUrl, entryForSend.videoUrl, entryForSend.previewUrl);
        if (!mediaSource) {
          updateSendStatus({
            state: 'error',
            message: 'No media is available to send for this sighting.',
          });
          return;
        }

        const confirmationMessage =
          options.confirmationMessage ||
          (isAlert
            ? 'Send this sighting as an alert to WhatsApp groups?'
            : 'Send this sighting to WhatsApp?');

        const confirmationWithClip = entryForSend.mediaType === 'video'
          ? `${confirmationMessage}\n\nVideo clip: ${formatWhatsAppClipSelection(videoClipSelection)}`
          : confirmationMessage;

        if (typeof window !== 'undefined' && !window.confirm(confirmationWithClip)) {
          updateSendStatus({ state: 'idle', message: '' });
          return;
        }

        if (
          isAlert &&
          typeof window !== 'undefined' &&
          !isDateToday(getCaptureTimestamp(entryForSend)) &&
          !window.confirm('‼️ The sighting was NOT TODAY, are you sure?')
        ) {
          updateSendStatus({ state: 'idle', message: '' });
          return;
        }

        if (isAlert && requestedCorrectionSpecies) {
          const change = describeSpeciesChange({ mode: 'animal', species: speciesToSend });
          const finalNote = buildCorrectionNote({
            actor: actorName,
            previousSpecies: entry.species,
            nextLabel: change.label,
            locationId: entry.locationId,
          });
          const authToken =
            typeof user?.getIdToken === 'function' ? await user.getIdToken() : null;

          correctionResult = await applySightingCorrection({
            entry,
            mode: 'animal',
            nextSpeciesName: speciesToSend,
            actor: actorName,
            note: finalNote,
            change,
            relocateMedia: true,
            authToken,
          });

          entryForSend = {
            ...entry,
            ...correctionResult.parentDocUpdates,
            species: correctionResult.change.label,
            mediaUrl: correctionResult.parentDocUpdates.mediaUrl ?? entry.mediaUrl,
            previewUrl: correctionResult.parentDocUpdates.previewUrl ?? entry.previewUrl,
            videoUrl: correctionResult.parentDocUpdates.videoUrl ?? entry.videoUrl,
            debugUrl: correctionResult.parentDocUpdates.debugUrl ?? entry.debugUrl,
            meta: entry.meta
              ? {
                ...entry.meta,
                parentDoc: {
                  ...(entry.meta.parentDoc || {}),
                  ...correctionResult.parentDocUpdates,
                },
                speciesDoc: {
                  ...(entry.meta.speciesDoc || {}),
                  ...(correctionResult.speciesDocUpdates || {}),
                  id: correctionResult.speciesDocId || entry.meta?.speciesDoc?.id,
                },
                speciesDocPath: correctionResult.speciesDocPath || entry.meta?.speciesDocPath,
              }
              : entry.meta,
          };
          speciesToSend = correctionResult.change.normalizedName;
          mediaSource = pickFirstSource(entryForSend.mediaUrl, entryForSend.videoUrl, entryForSend.previewUrl);
          if (!mediaSource) {
            updateSendStatus({
              state: 'error',
              message: 'No media is available to send for this sighting.',
            });
            return;
          }
        }

        trackButton(isAlert ? 'sightings_send_whatsapp_alert' : 'sightings_send_whatsapp');

        const payload = {
          locationId: entryForSend.locationId,
          gcp_url: mediaSource,
          media_url: mediaSource,
          timestamp:
            entryForSend.createdAt instanceof Date && !Number.isNaN(entryForSend.createdAt.getTime())
              ? entryForSend.createdAt.toISOString()
              : undefined,
          mediaType: entryForSend.mediaType,
          displaySpecies: requestedCorrectionSpecies || speciesToSend,
          species: speciesToSend,
          primarySpecies: speciesToSend,
          alertStyle,
          manualAlert: isAlert,
          clipSelection: videoClipSelection,
          clip_selection: videoClipSelection,
          parentPath: entryForSend?.meta?.parentPath || '',
          speciesDocPath: entryForSend?.meta?.speciesDocPath || '',
        };

        const response = await fetch(SEND_WHATSAPP_ENDPOINT, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        });

        const contentType = response.headers.get('content-type') || '';
        const responseBody = contentType.includes('application/json')
          ? await response.json().catch(() => ({}))
          : await response.text();

        if (!response.ok) {
          const errorMessage =
            typeof responseBody === 'string'
              ? responseBody
              : responseBody?.error || 'Failed to send WhatsApp alert';
          throw new Error(errorMessage);
        }

        const cooldownWarning = Boolean(
          isAlert
          && responseBody?.cooldown
          && responseBody.cooldown.recorded === false
          && responseBody.cooldown.reason !== 'custom_species',
        );

        trackEvent(isAlert ? 'sightings_send_whatsapp_alert_success' : 'sightings_send_whatsapp_success', {
          location: entryForSend.locationId,
          mediaType: entryForSend.mediaType,
          alertStyle: alertStyle || 'legacy',
          correctedBeforeSend: Boolean(correctionResult),
          clipSelection: entryForSend.mediaType === 'video' ? videoClipSelection : null,
          cooldownReset: responseBody?.cooldown?.recorded ?? null,
        });

        updateSendStatus({
          state: cooldownWarning ? 'error' : 'success',
          message: cooldownWarning
            ? 'Alert sent, but cooldown was not reset.'
            : (isAlert ? 'Alert sent to WhatsApp' : 'Sent to WhatsApp'),
        });

        if (correctionResult) {
          loadSightings({ pageIndex: currentPage });
        }
      } catch (err) {
        console.error('Failed to send WhatsApp alert', err);
        const message = err instanceof Error && err.message ? err.message : 'Failed to send to WhatsApp';

        trackEvent(isAlert ? 'sightings_send_whatsapp_alert_error' : 'sightings_send_whatsapp_error', {
          location: entryForSend.locationId,
          mediaType: entryForSend.mediaType,
          error: message,
          alertStyle: alertStyle || 'legacy',
          clipSelection: entryForSend.mediaType === 'video' ? videoClipSelection : null,
        });

        updateSendStatus({
          state: 'error',
          message,
        });
      } finally {
        sendingEntryIdsRef.current.delete(entryId);
      }
    },
    [actorName, currentPage, loadSightings, user],
  );

  const handleDownloadSighting = useCallback(
    async (entry, source = 'card') => {
      if (!entry) {
        return;
      }

      setDownloadStatusMap((prev) => ({
        ...prev,
        [entry.id]: { state: 'pending' },
      }));

      try {
        await downloadEntryMedia(entry);
        trackButton('sighting_download', {
          source,
          mediaType: entry.mediaType,
          species: entry.species,
          location: entry.locationId,
        });
      } catch (err) {
        console.error('Failed to download sighting media', err);
        if (typeof window !== 'undefined') {
          window.alert(err?.message || 'Unable to download this sighting.');
        }
      } finally {
        setDownloadStatusMap((prev) => ({
          ...prev,
          [entry.id]: { state: 'idle' },
        }));
      }
    },
    [],
  );

  const selectedFacebookPage = useMemo(
    () => facebookConnection.pages.find(
      (page) => page.id === facebookConnection.selectedPageId,
    ) || null,
    [facebookConnection.pages, facebookConnection.selectedPageId],
  );

  const handleOpenFacebookPublish = useCallback((entry, source = 'card') => {
    if (!entry || entry.mediaType !== 'video') {
      return;
    }
    if (facebookConnection.loading) {
      window.alert('TingVision is still checking your Facebook Page connection. Try again in a moment.');
      return;
    }
    if (!facebookConnection.connected || !selectedFacebookPage) {
      window.alert(
        facebookConnection.error
          || 'Connect and choose a Facebook Page in Settings before posting a video.',
      );
      return;
    }

    trackButton('sighting_facebook_review', {
      source,
      species: entry.species,
      location: entry.locationId,
      pageId: selectedFacebookPage.id,
    });
    setFacebookPublishTarget(entry);
    if (source === 'modal') {
      setActiveSighting(null);
    }
  }, [facebookConnection, selectedFacebookPage]);

  const handleFacebookPublished = useCallback((result) => {
    trackEvent('sighting_facebook_publish_success', {
      species: facebookPublishTarget?.species,
      location: facebookPublishTarget?.locationId,
      pageId: result?.pageId,
      duplicatePrevented: Boolean(result?.duplicatePrevented),
    });
  }, [facebookPublishTarget]);

  const handleDeleteSightings = useCallback(
    async (entries, options = {}) => {
      const { source = 'card' } = options;
      const targets = entries.filter((entry) => entry && entry.id);
      if (targets.length === 0) {
        return;
      }

      if (typeof window !== 'undefined') {
        const confirmationMessage = targets.length === 1
          ? 'Delete this sighting? This will remove its media files and hide it from the feed.'
          : `Delete ${targets.length} sightings? This will remove their media files and hide them from the feed.`;
        const confirmed = window.confirm(confirmationMessage);
        if (!confirmed) {
          return;
        }
      }

      trackButton('sightings_delete', {
        source,
        count: targets.length,
      });

      setDeleteStatusMap((prev) => {
        const next = { ...prev };
        targets.forEach((entry) => {
          next[entry.id] = { state: 'pending', message: 'Deleting…' };
        });
        return next;
      });

      let authToken = null;
      try {
        authToken = typeof user?.getIdToken === 'function' ? await user.getIdToken() : null;
      } catch (err) {
        authToken = null;
      }

      try {
        const results = await Promise.all(
          targets.map(async (entry) => {
            try {
              if (!DELETE_SIGHTING_ENDPOINT) {
                throw new Error('Delete endpoint is not configured.');
              }

              const parentPath = entry?.meta?.parentPath;
              if (!parentPath) {
                throw new Error('Sighting metadata is missing required references.');
              }

              if (!authToken) {
                throw new Error('Missing auth token for delete.');
              }

              const response = await fetch(DELETE_SIGHTING_ENDPOINT, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  Authorization: `Bearer ${authToken}`,
                },
                body: JSON.stringify({
                  parentPath,
                  actorName,
                }),
              });

              const contentType = response.headers.get('content-type') || '';
              const responseBody = contentType.includes('application/json')
                ? await response.json().catch(() => ({}))
                : await response.text();

              if (!response.ok) {
                const message =
                  typeof responseBody === 'string'
                    ? responseBody
                    : responseBody?.error || 'Unable to delete sighting.';
                throw new Error(message);
              }

              return { id: entry.id, status: 'success', message: 'Sighting deleted.' };
            } catch (err) {
              console.error('Failed to delete sighting', err);
              return {
                id: entry.id,
                status: 'error',
                message: err?.message || 'Unable to delete sighting.',
              };
            }
          }),
        );

        const deletedIds = results.filter(({ status }) => status === 'success').map(({ id }) => id);

        if (deletedIds.length > 0) {
          setSightings((prev) => prev.filter((item) => !deletedIds.includes(item.id)));
          setActiveSighting((prev) => (prev && deletedIds.includes(prev.id) ? null : prev));
          setSelectedSightings((prev) => {
            if (prev.size === 0) {
              return prev;
            }
            const next = new Set(prev);
            deletedIds.forEach((id) => next.delete(id));
            return next;
          });
        }

        const errorCount = results.length - deletedIds.length;
        trackEvent('sightings_delete_result', {
          source,
          successCount: deletedIds.length,
          errorCount,
        });

        setDeleteStatusMap((prev) => {
          const next = { ...prev };
          results.forEach(({ id, status, message }) => {
            next[id] = { state: status, message };
          });
          return next;
        });
      } catch (err) {
        console.error('Failed to delete sightings', err);
        trackEvent('sightings_delete_error', {
          source,
          count: targets.length,
          error: toAnalyticsError(err?.message || 'Failed to delete sightings'),
        });
      }
    },
    [actorName, user],
  );

  const handleToggleHighlight = useCallback(
    async (entry, source = 'card') => {
      if (!entry) {
        return;
      }

      const highlightKey = getHighlightStateKey(entry);
      if (!highlightKey) {
        return;
      }

      const nextEnabled = !entry.isHighlighted;

      setHighlightStatusMap((prev) => ({
        ...prev,
        [highlightKey]: {
          state: 'pending',
          message: nextEnabled ? 'Adding to highlights…' : 'Removing from highlights…',
        },
      }));

      try {
        const updates = await applySightingHighlight({
          entry,
          enabled: nextEnabled,
          actor: actorName,
        });

        syncHighlightStateForParent(entry?.meta?.parentPath, updates);
        setHighlightStatusMap((prev) => ({
          ...prev,
          [highlightKey]: {
            state: 'success',
            message: nextEnabled ? 'Added to highlights.' : 'Removed from highlights.',
          },
        }));
        trackButton('sighting_highlight_toggle', {
          enabled: nextEnabled,
          source,
          species: entry.species,
          location: entry.locationId,
        });
      } catch (err) {
        console.error('Failed to update sighting highlight', err);
        trackEvent('sighting_highlight_error', {
          enabled: nextEnabled,
          source,
          species: entry.species,
          location: entry.locationId,
          error: toAnalyticsError(err?.message || 'Unable to update highlight.'),
        });
        setHighlightStatusMap((prev) => ({
          ...prev,
          [highlightKey]: {
            state: 'error',
            message: err?.message || 'Unable to update highlight.',
          },
        }));
      }
    },
    [actorName, syncHighlightStateForParent],
  );


  const handleToggleSightingSelection = useCallback((sightingId, source = 'card') => {
    if (!sightingId) {
      return;
    }
    let nextSelected = false;
    setSelectedSightings((prev) => {
      const next = new Set(prev);
      if (next.has(sightingId)) {
        next.delete(sightingId);
        nextSelected = false;
      } else {
        next.add(sightingId);
        nextSelected = true;
      }
      return next;
    });
    const selectedEntry = sightings.find((entry) => entry.id === sightingId);
    trackButton('sightings_select_toggle', {
      source,
      selected: nextSelected,
      sightingId,
      species: selectedEntry?.species,
      location: selectedEntry?.locationId,
    });
  }, [sightings]);

  const handleSelectAllFiltered = useCallback(() => {
    let shouldSelect = false;
    const filteredIds = filteredSightings.map((entry) => entry.id);
    setSelectedSightings((prev) => {
      const next = new Set(prev);
      const shouldDeselect = filteredIds.length > 0 && filteredIds.every((id) => next.has(id));
      shouldSelect = !shouldDeselect;
      if (shouldDeselect) {
        filteredIds.forEach((id) => next.delete(id));
      } else {
        filteredIds.forEach((id) => next.add(id));
      }
      return next;
    });
    trackButton('sightings_select_page', {
      selected: shouldSelect,
      count: filteredIds.length,
    });
  }, [filteredSightings]);

  const handleClearSelection = useCallback(() => {
    trackButton('sightings_select_clear', { count: selectedSightings.size });
    setSelectedSightings(new Set());
  }, [selectedSightings.size]);

  const handleBulkDeleteSelected = useCallback((source = 'selection_bar') => {
    if (selectedSightings.size === 0) {
      return;
    }
    const selectedEntries = sightings.filter((entry) => selectedSightings.has(entry.id));
    handleDeleteSightings(selectedEntries, { source });
  }, [handleDeleteSightings, selectedSightings, sightings]);

  useEffect(() => {
    if (!activeSighting) {
      return;
    }

    const isStillVisible = isSightingVisibleOrLinked(activeSighting, filteredSightings, linkedSightingId);
    if (!isStillVisible) {
      setActiveSighting(null);
    }
  }, [filteredSightings, activeSighting, linkedSightingId]);

  useEffect(() => {
    if (!activeSighting) {
      return undefined;
    }

    const handleKeyDown = (event) => {
      const { key } = event;
      if (!['Escape', 'ArrowRight', 'ArrowLeft', 'ArrowUp', 'ArrowDown'].includes(key)) {
        return;
      }

      if (
        event.target instanceof HTMLElement
        && (event.target.isContentEditable
          || ['INPUT', 'TEXTAREA', 'SELECT'].includes(event.target.tagName))
      ) {
        return;
      }

      if (key === 'Escape') {
        handleCloseSighting('keyboard');
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      if (key === 'ArrowRight') {
        handleNavigateSighting(1, 'keyboard');
      }
      if (key === 'ArrowLeft') {
        handleNavigateSighting(-1, 'keyboard');
      }
      if (key === 'ArrowUp') {
        handleSetActiveSightingSelection(true, 'keyboard');
      }
      if (key === 'ArrowDown') {
        handleSetActiveSightingSelection(false, 'keyboard');
      }
    };

    window.addEventListener('keydown', handleKeyDown, { capture: true });
    return () => {
      window.removeEventListener('keydown', handleKeyDown, { capture: true });
    };
  }, [activeSighting, handleCloseSighting, handleNavigateSighting, handleSetActiveSightingSelection]);

  const renderModalContent = () => {
    if (!activeSighting) {
      return null;
    }

    const isDebugMode = modalViewMode === 'debug';
    const prefersVideo = activeSighting.mediaType === 'video';

    const standardVideoSrc = pickFirstSource(activeSighting.videoUrl);
    const hdVideoSrc = pickFirstSource(
      prefersVideo ? activeSighting.mediaUrl : null,
      activeSighting.videoUrl,
    );
    const motionTrackVideoSrc = getMotionTrackUrl(activeSighting);
    const standardImageSrc = pickFirstSource(activeSighting.previewUrl);
    const hdImageSrc = pickFirstSource(
      !prefersVideo ? activeSighting.mediaUrl : null,
      activeSighting.previewUrl,
    );

    const hasHdImageAlternative = Boolean(hdImageSrc && hdImageSrc !== standardImageSrc);
    const useHdImage = isHdEnabled && hasHdImageAlternative;
    const shouldForceHdVideo = prefersVideo && Boolean(hdVideoSrc);
    const fullVideoSrc = prefersVideo
      ? (shouldForceHdVideo ? hdVideoSrc : standardVideoSrc) || null
      : null;
    const useMotionTrack = Boolean(prefersVideo && motionTrackVideoSrc && !isFullClipEnabled);
    const shouldAutoPlayVideo = useMotionTrack || isFullClipEnabled;

    let selectedVideoSrc = null;
    let selectedImageSrc = null;

    selectedVideoSrc = prefersVideo
      ? (useMotionTrack ? motionTrackVideoSrc : fullVideoSrc)
      : null;
    selectedImageSrc = (!prefersVideo ? (useHdImage ? hdImageSrc : standardImageSrc) : null)
      || null;

    if (prefersVideo && !selectedVideoSrc) {
      selectedVideoSrc = (standardVideoSrc || hdVideoSrc || null);
    }

    if (!prefersVideo && !selectedImageSrc) {
      selectedImageSrc = (standardImageSrc || hdImageSrc || null);
    }

    if (prefersVideo && selectedVideoSrc) {
      return (
        <div className="sightingModal__videoReview">
          <video
            key={`video-${activeSighting.id}-${useMotionTrack ? 'motion' : 'full'}-${selectedVideoSrc}`}
            src={selectedVideoSrc}
            poster={standardImageSrc || undefined}
            controls
            autoPlay={shouldAutoPlayVideo}
            muted={useMotionTrack}
            loop={useMotionTrack}
            playsInline
            preload={shouldAutoPlayVideo ? 'metadata' : 'none'}
            onLoadedMetadata={(event) => {
              const playbackRate = getSightingVideoPlaybackRate(useMotionTrack);
              event.currentTarget.defaultPlaybackRate = playbackRate;
              event.currentTarget.playbackRate = playbackRate;
            }}
          />
          <div className="sightingModal__videoReviewControls">
            <span className="sightingModal__videoKind">
              {useMotionTrack ? 'Short motion track' : 'Full sighting clip'}
            </span>
            {motionTrackVideoSrc && fullVideoSrc && (
              <button
                type="button"
                className="sightingModal__videoSwitch"
                onClick={() => {
                  const nextValue = !isFullClipEnabled;
                  setIsFullClipEnabled(nextValue);
                  trackButton('sighting_toggle_full_clip', {
                    enabled: nextValue,
                    species: activeSighting?.species,
                    location: activeSighting?.locationId,
                  });
                }}
              >
                {useMotionTrack ? 'View full clip' : 'Use motion track'}
              </button>
            )}
          </div>
        </div>
      );
    }

    if (selectedImageSrc) {
      const debugBoxes = resolveEntryDebugBoxes(activeSighting);
      const shouldOverlay = (isDebugMode || isAnimalBoxesEnabled) && debugBoxes.length > 0;
      return (
        shouldOverlay ? (
          <DebugBoundingImage
            key={`img-${modalViewMode}-${selectedImageSrc}`}
            src={selectedImageSrc}
            alt={`${activeSighting.species} sighting enlarged`}
            boxes={debugBoxes}
            defaultLabel={activeSighting.species}
          />
        ) : (
          <img
            key={`img-${modalViewMode}-${selectedImageSrc}`}
            src={selectedImageSrc}
            alt={`${activeSighting.species} sighting enlarged`}
          />
        )
      );
    }

    if (selectedVideoSrc) {
      return (
        <video
          key={`fallback-video-${modalViewMode}-${selectedVideoSrc}`}
          src={selectedVideoSrc}
          poster={standardImageSrc || undefined}
          controls
          playsInline
          preload="none"
        />
      );
    }

    return <div className="sightingModal__placeholder">No media available</div>;
  };

  let statusMessage = null;
  let isStatusError = false;

  if (isAccessLoading) {
    statusMessage = 'Loading access…';
  } else if (loading) {
    statusMessage = 'Loading…';
  } else if (error) {
    statusMessage = error;
    isStatusError = true;
  } else if (accessError) {
    statusMessage = accessError;
    isStatusError = true;
  }

  return (
    <div className="sightingsPage">
      <div className="sightingsPage__inner">
        {sightingLinkMessage && <p role="status">{sightingLinkMessage}</p>}
        <header className="sightingsPage__header">
          <div>
            <h1>Recent Sightings</h1>
            <p>Latest activity sorted by capture time.</p>
          </div>
          <div className="sightingsPage__controls">
            <div className="sightingsPage__statusSlot" role="status" aria-live="polite">
              {statusMessage && (
                <span
                  className={`sightingsPage__status${isStatusError ? ' sightingsPage__status--error' : ''}`}
                >
                  {statusMessage}
                </span>
              )}
            </div>
            <div className="sightingsPage__filterGroup">
              {isAdmin && (
                <div className="sightingsPage__field sightingsPage__field--checkbox">
                  <label className="sightingsPage__checkboxLabel" htmlFor="debugViewToggle">
                    <input
                      id="debugViewToggle"
                      type="checkbox"
                      checked={isDebugViewEnabled}
                      onChange={(event) => handleDebugViewToggle(event.target.checked)}
                    />
                    <span>Debug</span>
                  </label>
                </div>
              )}
              {isAdmin && (
                <div className="sightingsPage__field sightingsPage__field--checkbox">
                  <label className="sightingsPage__checkboxLabel" htmlFor="motionMaskToggle">
                    <input
                      id="motionMaskToggle"
                      type="checkbox"
                      checked={isMotionMaskEnabled}
                      onChange={(event) => handleMotionMaskToggle(event.target.checked)}
                    />
                    <span>Motion</span>
                  </label>
                </div>
              )}
              <div className="sightingsPage__field sightingsPage__field--checkbox">
                <label className="sightingsPage__checkboxLabel" htmlFor="animalBoxesToggle">
                  <input
                    id="animalBoxesToggle"
                    type="checkbox"
                    checked={isAnimalBoxesEnabled}
                    onChange={(event) => handleAnimalBoxesToggle(event.target.checked)}
                  />
                  <span>Boxes</span>
                </label>
              </div>
              <div className="sightingsPage__field sightingsPage__field--multiselect" ref={speciesMenuRef}>
                <label id="speciesFilterLabel" htmlFor="speciesFilterTrigger">Species</label>
                <button
                  type="button"
                  id="speciesFilterTrigger"
                  className={`multiSelect__trigger${isSpeciesMenuOpen ? ' is-open' : ''}`}
                  aria-haspopup="true"
                  aria-expanded={isSpeciesMenuOpen}
                  onClick={handleSpeciesMenuToggle}
                >
                  {selectedSpeciesSummary}
                </button>
                {isSpeciesMenuOpen && (
                  <div className="multiSelect__menu" role="listbox" aria-labelledby="speciesFilterLabel">
                    <div className="multiSelect__actions">
                      <button
                        type="button"
                        className="multiSelect__clear"
                        onClick={() => {
                          handleSpeciesClear();
                          setIsSpeciesMenuOpen(false);
                        }}
                        disabled={selectedSpecies.length === 0}
                      >
                        Clear selection
                      </button>
                      <label className="multiSelect__mode">
                        <span>Filter mode:</span>
                        <select value={speciesFilterMode} onChange={handleSpeciesModeChange}>
                          <option value="include">Only selected</option>
                          <option value="exclude">All except selected</option>
                        </select>
                      </label>
                    </div>
                    <ul className="multiSelect__list">
                      {availableSpecies.length === 0 && (
                        <li className="multiSelect__empty">No species available</li>
                      )}
                      {availableSpecies.map(({ value, label }) => {
                        const isChecked = selectedSpecies.includes(value);
                        return (
                          <li key={value} className="multiSelect__option">
                            <label>
                              <input
                                type="checkbox"
                                checked={isChecked}
                                onChange={() => handleSpeciesToggle(value)}
                              />
                              <span>{label}</span>
                            </label>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}
              </div>
              <div className="sightingsPage__field">
                <label htmlFor="locationFilter">Location</label>
                <select
                  id="locationFilter"
                  value={locationFilter}
                  onChange={handleLocationFilterChange}
                >
                  <option value="all">All locations</option>
                  {availableLocationOptions.length > 0 && (
                    <optgroup label="Sightings from">
                      {availableLocationOptions.map(({ value, label }, index) => (
                        <option key={value} value={value}>
                          {index + 1}. {label}
                        </option>
                      ))}
                    </optgroup>
                  )}
                  {locationsWithoutSightings.length > 0 && (
                    <optgroup label="None from">
                      {locationsWithoutSightings.map(({ value, label }, index) => (
                        <option key={value} value={value} disabled>
                          {index + 1}. {label}
                        </option>
                      ))}
                    </optgroup>
                  )}
                </select>
              </div>
              <div className="sightingsPage__field">
                <label htmlFor="mediaFilter">Media</label>
                <select
                  id="mediaFilter"
                  value={mediaTypeFilter}
                  onChange={handleMediaTypeFilterChange}
                >
                  <option value="all">All types</option>
                  <option value="motion-track">Motion tracks</option>
                  <option value="video">Video</option>
                  <option value="image">Image</option>
                </select>
              </div>
            </div>
            <button
              type="button"
              className="sightingsPage__refresh"
              onClick={() => {
                trackButton('sightings_refresh');
                pendingRefreshScrollRef.current = true;
                loadSightings({ pageIndex: currentPage, highlightNew: true });
              }}
              disabled={loading || paginationLoading}
            >
              Refresh
            </button>
          </div>
        </header>

        {isAdmin && hasAnySightings && (
          <div className="sightingsPage__selectionBar" role="status" aria-live="polite">
            <div className="sightingsPage__selectionStatus">
              {selectedSightingsCount > 0
                ? `${selectedSightingsCount} sighting${selectedSightingsCount === 1 ? '' : 's'} selected`
                : 'Select sightings to delete multiple at once.'}
            </div>
            <div className="sightingsPage__selectionActions">
              <button
                type="button"
                className="sightingsPage__selectionButton"
                onClick={handleSelectAllFiltered}
                disabled={filteredSightings.length === 0}
              >
                {isAllFilteredSelected ? 'Deselect all on page' : 'Select all on page'}
              </button>
              <button
                type="button"
                className="sightingsPage__selectionButton"
                onClick={handleClearSelection}
                disabled={selectedSightingsCount === 0}
              >
                Clear selection
              </button>
              <button
                type="button"
                className="sightingCard__actionsButton sightingCard__actionsButton--danger sightingsPage__selectionDelete"
                onClick={() => handleBulkDeleteSelected('selection_bar')}
                disabled={selectedSightingsCount === 0 || hasPendingSelectedDeletes}
              >
                {hasPendingSelectedDeletes
                  ? 'Deleting…'
                  : `Delete selected (${selectedSightingsCount})`}
              </button>
            </div>
          </div>
        )}

        {editFeedback.text && (
          <div
            className={`sightingsPage__alert sightingsPage__alert--${editFeedback.type === 'error' ? 'error' : 'success'}`}
          >
            <span>{editFeedback.text}</span>
            <button
              type="button"
              className="sightingsPage__alertClose"
              onClick={handleDismissFeedback}
              aria-label="Dismiss correction message"
            >
              Dismiss
            </button>
          </div>
        )}

        {noAssignedLocations && (
          <div className="sightingsPage__empty">No locations have been assigned to your account yet.</div>
        )}

        {!loading && !error && !hasAnySightings && !noAssignedLocations && (
          <div className="sightingsPage__empty">No sightings have been recorded yet.</div>
        )}

        {!loading && !error && hasAnySightings && !hasSightings && (
          <div className="sightingsPage__empty">No sightings match the selected filters.</div>
        )}

        <div className="sightingsPage__list">
          {filteredSightings.map((entry) => {
            const sendStatus = sendStatusMap[entry.id] || { state: 'idle', message: '' };
            const isSending = sendStatus.state === 'pending';
            const deleteStatus = deleteStatusMap[entry.id] || { state: 'idle', message: '' };
            const isDeleting = deleteStatus.state === 'pending';
            const downloadStatus = downloadStatusMap[entry.id] || { state: 'idle' };
            const isDownloading = downloadStatus.state === 'pending';
            const highlightStatusKey = getHighlightStateKey(entry);
            const highlightStatus = highlightStatusMap[highlightStatusKey] || { state: 'idle', message: '' };
            const isHighlighting = highlightStatus.state === 'pending';
            const isSelected = selectedSightings.has(entry.id);
            const isNew = newSightingIds.has(entry.id);
            const confidenceClass = getConfidenceClass(entry.maxConf);
            const pickDebugField = (key) => pickEntryDebugValue(entry, key);
            const rawTrigger = resolveEntryTrigger(entry);
            const presetValue = resolveEntryPresetValue(entry, rawTrigger);
            const mlConfidence = resolveEntryMlConfidence(entry, rawTrigger);
            const mlBackend = resolveEntryMlBackend(entry, rawTrigger);
            const tourClassifierPredictions = resolveEntryTourClassifierPredictions(entry, rawTrigger);
            const geminiReview = resolveEntryGeminiReview(entry);
            const differentGeminiPrediction = formatDifferentGeminiPrediction(
              entry.species,
              geminiReview,
            );
            const sightingTitle = `${formatCountWithSpecies(entry.species, entry.count)}${
              differentGeminiPrediction ? ` (${differentGeminiPrediction})` : ''
            }`;
            const debugFields = [
              { key: 'docid', value: entry.id },
              { key: 'Preset', value: presetValue },
              ...(mlBackend
                ? [{ key: 'ML_BACKEND', value: mlBackend }]
                : []),
              ...(mlConfidence !== null
                ? [{ key: 'ML_CONFIDENCE', value: mlConfidence }]
                : []),
              { key: 'TRIGGER', value: buildDebugTrigger(rawTrigger, mlConfidence) },
              ...(tourClassifierPredictions
                ? [{ key: 'TOUR_CLASSIFIER_PREDICTIONS', value: tourClassifierPredictions }]
                : []),
              ...buildGeminiDebugFields(geminiReview),
            ];
            const fgMaskUrl = pickFirstSource(pickDebugField('fgMaskUrl'), entry.fgMaskUrl);
            const debugBoxes = resolveEntryDebugBoxes(entry);
            const megadetectorVerify = resolveMegadetectorVerify(
              entry.megadetectorVerify,
              pickDebugField('megadetector_verify'),
            );
            const isMegadetectorFailed = Boolean(megadetectorVerify && megadetectorVerify.passed === false);
            const secondaryReason =
              isMegadetectorFailed && typeof megadetectorVerify.reason === 'string'
                ? megadetectorVerify.reason
                : '';
            const secondaryReasonLabel = (() => {
              if (!secondaryReason) {
                return '';
              }
              if (secondaryReason === 'no_md_detections') {
                return 'no animals';
              }
              return secondaryReason.replace(/_/g, ' ');
            })();
            const secondaryVerificationMessage = isMegadetectorFailed
              ? `Secondary animal identification failed${secondaryReasonLabel ? ` - ${secondaryReasonLabel}` : ''}`
              : '';
            const shouldShowDebugOverlay = (isDebugViewEnabled || isAnimalBoxesEnabled)
              && debugBoxes.length > 0;
            const captureTimestamp = getCaptureTimestamp(entry);
            const captureLabel = captureTimestamp ? formatCompactTimestampLabel(captureTimestamp) : '';
            const locationLabel = getLocationLabel(entry.locationId);
            const hasStatusTags = entry.isHighlighted || isMegadetectorFailed;
            const motionTrackVideoSrc = getMotionTrackUrl(entry);
            return (
              <article
                className={`sightingCard ${confidenceClass}${entry.isHighlighted ? ' sightingCard--highlighted' : ''}${isNew ? ' sightingCard--new' : ''}`}
                key={entry.id}
                data-sighting-id={entry.id}
              >
                <div
                  className={`sightingCard__media${motionTrackVideoSrc ? ' sightingCard__media--motionTrack' : ''}`}
                >
                  <button
                    type="button"
                    className="sightingCard__mediaButton"
                    onClick={() => handleOpenSighting(entry, { startWithFullClip: true })}
                    aria-label={`Open ${entry.mediaType} preview for ${entry.species}`}
                  >
                    {(() => {
                      const hdVideoSrc = entry.mediaType === 'video' ? entry.mediaUrl : null;
                      const cardVideoSrc = pickFirstSource(entry.videoUrl, hdVideoSrc);
                      const cardImageSrc = pickFirstSource(
                        entry.previewUrl,
                        entry.mediaType !== 'video' ? entry.mediaUrl : null,
                      );

                      if (shouldShowDebugOverlay && cardImageSrc) {
                        return (
                          <DebugBoundingImage
                            src={cardImageSrc}
                            alt={`${entry.species} debug bounding preview`}
                            boxes={debugBoxes}
                            defaultLabel={entry.species}
                          />
                        );
                      }

                      if (motionTrackVideoSrc) {
                        return (
                          <ManagedMotionTrackPreview
                            videoSrc={motionTrackVideoSrc}
                            posterSrc={cardImageSrc}
                            ariaLabel={`${entry.species} short motion track`}
                          />
                        );
                      }

                      if (cardImageSrc) {
                        return (
                          <img
                            src={cardImageSrc}
                            alt={`${entry.species} sighting`}
                            loading="lazy"
                            decoding="async"
                          />
                        );
                      }

                      if (entry.mediaType === 'video' && cardVideoSrc) {
                        return (
                          <div className="sightingCard__placeholder">Click to open video</div>
                        );
                      }

                      return <div className="sightingCard__placeholder">No preview available</div>;
                    })()}
                    <span className="sightingCard__badge">
                      {entry.mediaType === 'video' && motionTrackVideoSrc
                        ? 'Motion track'
                        : (entry.mediaType === 'video' ? 'Video' : 'Image')}
                    </span>
                  </button>
                </div>
                <div className="sightingCard__body">
                  {isAdmin && isMotionMaskEnabled && fgMaskUrl && (
                    <div className="sightingCard__motion">
                      <span className="sightingCard__motionLabel">Motion mask</span>
                      <img
                        src={fgMaskUrl}
                        alt={`${entry.species} motion mask`}
                        className="sightingCard__motionImage"
                        loading="lazy"
                      />
                    </div>
                  )}
                  <div className="sightingCard__header">
                    <div className="sightingCard__titleBlock">
                      <h3>{sightingTitle}</h3>
                      {!(typeof entry.count === 'number' && !Number.isNaN(entry.count) && entry.count > 0) && (
                        <span className="sightingCard__subtitle">{entry.species}</span>
                      )}
                    </div>
                    <div className="sightingCard__headerAside">
                      {typeof entry.maxConf === 'number' && (
                        <span
                          className={`sightingCard__confidence ${confidenceClass.replace('sightingCard--', 'sightingCard__confidence--')}`}
                        >
                          {formatPercent(entry.maxConf)}
                        </span>
                      )}
                      {isAdmin && (
                        <div className="sightingCard__selector">
                          <label>
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => handleToggleSightingSelection(entry.id, 'card')}
                              disabled={isDeleting}
                            />
                            <span>Select</span>
                          </label>
                        </div>
                      )}
                    </div>
                  </div>
                  {hasStatusTags && (
                    <div className="sightingCard__meta">
                      {entry.isHighlighted && (
                        <span className="sightingCard__highlightTag is-visible">
                          <HighlightIcon filled />
                          Highlighted
                        </span>
                      )}
                      {isMegadetectorFailed && (
                        <span className="sightingCard__megadetector">
                          {secondaryVerificationMessage}
                        </span>
                      )}
                    </div>
                  )}
                  {isDebugViewEnabled && (
                    <div className="sightingCard__debug">
                      <div className="sightingCard__debugHeader">
                        <span className="sightingCard__debugLabel">Debug</span>
                      </div>
                      <div className="sightingCard__debugGrid">
                        {debugFields.map(({ key, value }) => {
                          const isBlock = isDebugBlockValue(value);
                          const formattedValue = formatDebugValue(value);
                          return (
                            <div
                              key={key}
                              className={`sightingCard__debugStat${isBlock ? ' sightingCard__debugStat--block' : ''}`}
                            >
                              <span className="sightingCard__debugStatLabel">{key}</span>
                              {isBlock ? (
                                <pre className="sightingCard__debugStatValue">{formattedValue}</pre>
                              ) : (
                                <span className="sightingCard__debugStatValue">{formattedValue}</span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  <div className="sightingCard__summary">
                    <span className="sightingCard__location" title={entry.locationId}>
                      {locationLabel}
                    </span>
                    {captureTimestamp && (
                      <>
                        <span className="sightingCard__summaryDivider" aria-hidden="true">•</span>
                        <time dateTime={captureTimestamp.toISOString()}>
                          {captureLabel}
                        </time>
                      </>
                    )}
                  </div>
                  <div className="sightingCard__actions">
                    {isAdmin && (
                      <div className="sightingCard__quickActions">
                        <button
                          type="button"
                          className="sightingCard__quickActionButton sightingCard__quickActionButton--edit"
                          onClick={() => handleOpenEditModal(entry, 'card')}
                          disabled={editSaving || isDeleting || isHighlighting}
                          aria-label={`Edit sighting for ${entry.species}`}
                          title="Edit sighting"
                        >
                          <FiEdit2 />
                        </button>
                        <button
                          type="button"
                          className={`sightingCard__quickActionButton sightingCard__quickActionButton--highlight${entry.isHighlighted ? ' is-active' : ''}`}
                          onClick={() => handleToggleHighlight(entry)}
                          disabled={isHighlighting || isDeleting}
                          aria-label={entry.isHighlighted ? `Remove highlight from ${entry.species}` : `Highlight ${entry.species}`}
                          title={entry.isHighlighted ? 'Remove highlight' : 'Highlight'}
                        >
                          <HighlightIcon filled={entry.isHighlighted} />
                        </button>
                        <button
                          type="button"
                          className="sightingCard__quickActionButton sightingCard__quickActionButton--download"
                          onClick={() => handleDownloadSighting(entry)}
                          disabled={isDeleting || isDownloading}
                          aria-label={`${isDownloading ? 'Preparing download' : 'Download media'} for ${entry.species}`}
                          title={isDownloading ? 'Preparing download…' : 'Download'}
                        >
                          {isDownloading ? <span className="sightingCard__buttonSpinner" /> : <FiDownload />}
                        </button>
                        <button
                          type="button"
                          className="sightingCard__quickActionButton sightingCard__quickActionButton--danger"
                          onClick={() => handleDeleteSightings([entry], { source: 'card' })}
                          disabled={isDeleting || isHighlighting}
                          aria-label={`Delete ${entry.species} sighting`}
                          title="Delete"
                        >
                          <FiTrash2 />
                        </button>
                      </div>
                    )}
                    <div className="sightingCard__actionsRow">
                      {!isAdmin && (
                        <button
                          type="button"
                          className="sightingCard__actionsButton sightingCard__actionsButton--download"
                          onClick={() => handleDownloadSighting(entry)}
                          disabled={isDeleting || isDownloading}
                        >
                          {isDownloading ? <span className="sightingCard__buttonSpinner" /> : <FiDownload />}
                          {isDownloading ? 'Preparing…' : 'Download'}
                        </button>
                      )}
                      <button
                        type="button"
                        className="sightingCard__actionsButton"
                        onClick={() => handleSendToWhatsApp(entry)}
                        disabled={isSending || isDeleting || isHighlighting}
                      >
                        {isSending ? 'Sending…' : 'WhatsApp'}
                      </button>
                      {entry.mediaType === 'video' && (
                        <button
                          type="button"
                          className="sightingCard__actionsButton sightingCard__actionsButton--facebook"
                          onClick={() => handleOpenFacebookPublish(entry)}
                          disabled={isDeleting || isHighlighting}
                          title={selectedFacebookPage
                            ? `Review post to ${selectedFacebookPage.name}`
                            : 'Connect a Facebook Page in Settings'}
                        >
                          <FaFacebookF />
                          Facebook
                        </button>
                      )}
                      <button
                        type="button"
                        className="sightingCard__actionsButton sightingCard__actionsButton--alert"
                        onClick={() =>
                          handleSendToWhatsApp(entry, {
                            alertStyle: 'emoji',
                              confirmationMessage: 'Send this sighting as an alert to WhatsApp groups?',
                            })
                        }
                        disabled={isSending || isDeleting || isHighlighting}
                      >
                        {isSending ? 'Sending…' : 'Alert'}
                      </button>
                    </div>
                    {isAdmin && selectedSightingsCount > 1 && (
                      <div className="sightingCard__bulkActions">
                        <button
                          type="button"
                          className="sightingCard__actionsButton sightingCard__actionsButton--danger sightingCard__bulkDelete"
                          onClick={() => handleBulkDeleteSelected('card')}
                          disabled={hasPendingSelectedDeletes}
                        >
                          {hasPendingSelectedDeletes
                            ? 'Deleting selected…'
                            : `Delete selected (${selectedSightingsCount})`}
                        </button>
                      </div>
                    )}
                    {sendStatus.state === 'success' && sendStatus.message && (
                      <span className="sightingCard__actionsMessage sightingCard__actionsMessage--success">
                        {sendStatus.message}
                      </span>
                    )}
                    {sendStatus.state === 'error' && sendStatus.message && (
                      <span className="sightingCard__actionsMessage sightingCard__actionsMessage--error">
                        {sendStatus.message}
                      </span>
                    )}
                    {isAdmin && highlightStatus.state === 'success' && highlightStatus.message && (
                      <span className="sightingCard__actionsMessage sightingCard__actionsMessage--success">
                        {highlightStatus.message}
                      </span>
                    )}
                    {isAdmin && highlightStatus.state === 'error' && highlightStatus.message && (
                      <span className="sightingCard__actionsMessage sightingCard__actionsMessage--error">
                        {highlightStatus.message}
                      </span>
                    )}
                    {isAdmin && deleteStatus.state === 'success' && deleteStatus.message && (
                      <span className="sightingCard__actionsMessage sightingCard__actionsMessage--success">
                        {deleteStatus.message}
                      </span>
                    )}
                    {isAdmin && deleteStatus.state === 'error' && deleteStatus.message && (
                      <span className="sightingCard__actionsMessage sightingCard__actionsMessage--error">
                        {deleteStatus.message}
                      </span>
                    )}
                  </div>
                </div>
              </article>
            );
          })}
        </div>

        {(hasAnySightings || hasPreviousPage) && (
          <div className="sightingsPage__pagination" role="navigation" aria-label="Sightings pagination">
            <button
              type="button"
              className="sightingsPage__pageButton"
              onClick={handlePreviousPage}
              disabled={!hasPreviousPage || loading || paginationLoading}
            >
              Previous
            </button>
            <span className="sightingsPage__pageStatus">
              Page {currentPage + 1}{paginationLoading ? ' · Loading…' : ''}
            </span>
            <button
              type="button"
              className="sightingsPage__pageButton"
              onClick={handleNextPage}
              disabled={!hasMore || loading || paginationLoading}
            >
              Next
            </button>
          </div>
        )}
      </div>

      {editTarget && (
        <div
          className="sightingEditModal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="sightingEditModalTitle"
          onClick={() => handleCloseEditModal('overlay')}
        >
          <div
            className="sightingEditModal__content"
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              className="sightingEditModal__close"
              onClick={() => handleCloseEditModal('button')}
              disabled={editSaving}
            >
              Close
            </button>
            <h2 id="sightingEditModalTitle" className="sightingEditModal__title">Edit sighting</h2>
            <form onSubmit={handleSubmitEdit} className="sightingEditModal__form">
              <div className="sightingEditModal__section">
                <span className="sightingEditModal__label">Current classification</span>
                <p className="sightingEditModal__current">{editTarget.species || 'Unknown'}</p>
              </div>
              <div className="sightingEditModal__section">
                <span className="sightingEditModal__label">Update classification</span>
                <div className="sightingEditModal__radioGroup">
                  <label>
                    <input
                      type="radio"
                      name="sightingEditMode"
                      value="animal"
                      checked={editMode === 'animal'}
                      onChange={handleEditModeChange}
                      disabled={editSaving}
                    />
                    <span>Animal</span>
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="sightingEditMode"
                      value="background"
                      checked={editMode === 'background'}
                      onChange={handleEditModeChange}
                      disabled={editSaving}
                    />
                    <span>Background</span>
                  </label>
                </div>
              </div>
              {editMode === 'animal' && (
                <div className="sightingEditModal__section">
                  <label className="sightingEditModal__label" htmlFor="sightingEditSpecies">
                    Species
                  </label>
                  <input
                    id="sightingEditSpecies"
                    type="text"
                    value={editSpeciesInput}
                    onChange={(event) => setEditSpeciesInput(event.target.value)}
                    ref={editSpeciesInputRef}
                    className="sightingEditModal__input"
                    placeholder="Enter species name"
                    list="sightingEditSpeciesOptions"
                    disabled={editSaving}
                    required
                  />
                  <datalist id="sightingEditSpeciesOptions">
                    {availableSpecies.map(({ label }) => (
                      <option key={label} value={label} />
                    ))}
                  </datalist>
                </div>
              )}
              <div className="sightingEditModal__section">
                <span className="sightingEditModal__label">Notes</span>
                <p className="sightingEditModal__note">
                  {editNotePreview || 'A note describing this correction will be recorded.'}
                </p>
              </div>
              {editError && <p className="sightingEditModal__error">{editError}</p>}
              <div className="sightingEditModal__actions">
                <button
                  type="button"
                  className="sightingEditModal__secondary"
                  onClick={() => handleCloseEditModal('cancel')}
                  disabled={editSaving}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="sightingEditModal__primary"
                  disabled={editSaving}
                >
                  {editSaving ? 'Saving…' : 'Save changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {activeSighting && (
        <div
          className="sightingModal"
          role="dialog"
          aria-modal="true"
          onClick={() => handleCloseSighting('overlay')}
        >
          <div
            className={`sightingModal__content${isAdmin && isMotionMaskEnabled ? ' sightingModal__content--motion' : ''}`}
            onClick={(event) => event.stopPropagation()}
          >
            {(() => {
              const sendStatus = sendStatusMap[activeSighting.id] || { state: 'idle', message: '' };
              const deleteStatus = deleteStatusMap[activeSighting.id] || { state: 'idle', message: '' };
              const downloadStatus = downloadStatusMap[activeSighting.id] || { state: 'idle' };
              const highlightStatusKey = getHighlightStateKey(activeSighting);
              const highlightStatus = highlightStatusMap[highlightStatusKey] || { state: 'idle', message: '' };
              const isSending = sendStatus.state === 'pending';
              const isDeleting = deleteStatus.state === 'pending';
              const isDownloading = downloadStatus.state === 'pending';
              const isHighlighting = highlightStatus.state === 'pending';
              const currentIndex = filteredSightings.findIndex((entry) => entry.id === activeSighting.id);
              const hasPrevious = currentIndex > 0;
              const hasNext = currentIndex !== -1 && currentIndex < filteredSightings.length - 1;
              const shouldShowNav = filteredSightings.length > 1;
              const fgMaskUrl = pickFirstSource(
                pickEntryDebugValue(activeSighting, 'fgMaskUrl'),
                activeSighting.fgMaskUrl,
              );
              return (
                <>
                  <button
                    type="button"
                    className="sightingModal__close"
                    onClick={() => handleCloseSighting('button')}
                    aria-label="Close sighting preview"
                  >
                    Close
                  </button>
                  <div className="sightingModal__media">{renderModalContent()}</div>
                  {isAdmin && isMotionMaskEnabled && fgMaskUrl && (
                    <div className="sightingModal__motion">
                      <span className="sightingModal__motionLabel">Motion mask</span>
                      <img src={fgMaskUrl} alt={`${activeSighting.species} motion mask`} />
                    </div>
                  )}
                  {(() => {
                    const prefersVideo = activeSighting.mediaType === 'video';
                    const debugBoxes = resolveEntryDebugBoxes(activeSighting);
                    const hasAnimalBoxes = !prefersVideo && debugBoxes.length > 0;
                    const standardImageSrc = pickFirstSource(activeSighting.previewUrl);
                    const hdImageSrc = pickFirstSource(
                      !prefersVideo ? activeSighting.mediaUrl : null,
                      activeSighting.previewUrl,
                    );
                    const hasHdImageAlternative = Boolean(
                      !prefersVideo && hdImageSrc && hdImageSrc !== standardImageSrc,
                    );
                    const hasDebugMedia = Boolean(pickFirstSource(activeSighting.debugUrl));
                    const isDebugMode = modalViewMode === 'debug';
                    const shouldShowToggles = hasHdImageAlternative
                      || hasDebugMedia
                      || hasAnimalBoxes
                      || isAdmin;
                    return (
                      <div className="sightingModal__controls">
                        {(shouldShowNav || isAdmin) && (
                          <div className="sightingModal__nav">
                            {shouldShowNav && (
                              <>
                                <button
                                  type="button"
                                  className="sightingModal__navButton"
                                  onClick={() => handleNavigateSighting(-1, 'modal')}
                                  disabled={!hasPrevious}
                                >
                                  ← Previous
                                </button>
                                <button
                                  type="button"
                                  className="sightingModal__navButton"
                                  onClick={() => handleNavigateSighting(1, 'modal')}
                                  disabled={!hasNext}
                                >
                                  Next →
                                </button>
                              </>
                            )}
                            {isAdmin && (
                              <label className="sightingModal__selection">
                                <input
                                  type="checkbox"
                                  checked={selectedSightings.has(activeSighting.id)}
                                  onChange={() => handleToggleSightingSelection(activeSighting.id, 'modal')}
                                />
                                <span>
                                  {selectedSightings.has(activeSighting.id)
                                    ? 'Selected for deletion'
                                    : 'Select for deletion'}
                                </span>
                              </label>
                            )}
                          </div>
                        )}
                        {shouldShowToggles && (
                          <div className="sightingModal__toggles">
                            {hasHdImageAlternative && (
                              <button
                                type="button"
                                className={`sightingModal__toggle${isHdEnabled ? ' is-active' : ''}`}
                                onClick={() => {
                                  const nextValue = !isHdEnabled;
                                  setIsHdEnabled(nextValue);
                                  trackButton('sighting_toggle_hd', {
                                    enabled: nextValue,
                                    species: activeSighting?.species,
                                    location: activeSighting?.locationId,
                                  });
                                }}
                              >
                                {isHdEnabled ? 'Standard Quality' : 'View in HD'}
                              </button>
                            )}
                            {hasDebugMedia && (
                              <button
                                type="button"
                                className={`sightingModal__toggle${isDebugMode ? ' is-active' : ''}`}
                                onClick={() => {
                                  const nextMode = modalViewMode === 'debug' ? 'standard' : 'debug';
                                  setModalViewMode(nextMode);
                                  trackButton('sighting_toggle_view', {
                                    mode: nextMode,
                                    species: activeSighting?.species,
                                    location: activeSighting?.locationId,
                                  });
                                }}
                              >
                                {isDebugMode ? 'Standard View' : 'Debug'}
                              </button>
                            )}
                            {hasAnimalBoxes && (
                              <button
                                type="button"
                                className={`sightingModal__toggle${isAnimalBoxesEnabled ? ' is-active' : ''}`}
                                onClick={() => handleAnimalBoxesToggle(!isAnimalBoxesEnabled, 'modal')}
                              >
                                {isAnimalBoxesEnabled ? 'Hide Animal Boxes' : 'Animal Boxes'}
                              </button>
                            )}
                            {isAdmin && (
                              <button
                                type="button"
                                className={`sightingModal__toggle${isMotionMaskEnabled ? ' is-active' : ''}`}
                                onClick={() => {
                                  const nextValue = !isMotionMaskEnabled;
                                  handleMotionMaskToggle(nextValue, 'modal');
                                }}
                              >
                                {isMotionMaskEnabled ? 'Hide Motion' : 'Motion'}
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })()}
                  <div className="sightingModal__details">
                    <h3>{activeSighting.species}</h3>
                    {activeSighting.meta?.parentDoc?.isMotionPreview && <p>Motion preview · no full recording</p>}
                    <div className="sightingModal__meta">
                      {(() => {
                        const captureTimestamp = getCaptureTimestamp(activeSighting);
                        if (!captureTimestamp) return null;
                        return (
                          <div className="sightingModal__metaRow">
                            <span className="sightingModal__metaLabel">Captured</span>
                            <time dateTime={captureTimestamp.toISOString()}>
                              {formatTimestampLabel(captureTimestamp)}
                            </time>
                          </div>
                        );
                      })()}
                      {activeSighting.locationId && (
                        <div className="sightingModal__metaRow">
                          <span className="sightingModal__metaLabel">Location</span>
                          <span className="sightingModal__location" title={activeSighting.locationId}>
                            {activeSightingLocationLabel}
                          </span>
                        </div>
                      )}
                      {modalViewMode === 'debug' && (() => {
                        const activeRawTrigger = resolveEntryTrigger(activeSighting);
                        const activePresetValue = resolveEntryPresetValue(activeSighting, activeRawTrigger);
                        if (activePresetValue === null) {
                          return null;
                        }
                        return (
                          <div className="sightingModal__metaRow">
                            <span className="sightingModal__metaLabel">Preset</span>
                            <span>{formatPresetValue(activePresetValue)}</span>
                          </div>
                        );
                      })()}
                      {activeSighting.isHighlighted && (
                        <div className="sightingModal__metaRow">
                          <span className="sightingModal__metaLabel">Highlight</span>
                          <span className="sightingCard__highlightTag is-visible">
                            <HighlightIcon filled />
                            Starred
                          </span>
                        </div>
                      )}
                    </div>
                    <div className="sightingModal__admin">
                      <div className="sightingModal__actions">
                        {isAdmin && (
                          <button
                            type="button"
                            className="sightingCard__actionsButton"
                            onClick={() => {
                              handleOpenEditModal(activeSighting, 'modal');
                              handleCloseSighting('edit');
                            }}
                            disabled={editSaving || isDeleting || isHighlighting}
                          >
                            Edit sighting
                          </button>
                        )}
                        {isAdmin && (
                          <button
                            type="button"
                            className={`sightingCard__actionsButton sightingCard__actionsButton--highlight${activeSighting.isHighlighted ? ' is-active' : ''}`}
                            onClick={() => handleToggleHighlight(activeSighting, 'modal')}
                            disabled={isHighlighting || isDeleting}
                          >
                          <HighlightIcon filled={activeSighting.isHighlighted} />
                          {isHighlighting
                            ? (activeSighting.isHighlighted ? 'Removing…' : 'Starring…')
                            : (activeSighting.isHighlighted ? 'Highlighted' : 'Highlight')}
                          </button>
                        )}
                        <button
                          type="button"
                          className="sightingCard__actionsButton sightingCard__actionsButton--download"
                          onClick={() => handleDownloadSighting(activeSighting, 'modal')}
                          disabled={isDeleting || isDownloading}
                        >
                          {isDownloading ? <span className="sightingCard__buttonSpinner" /> : <FiDownload />}
                          {isDownloading ? 'Preparing…' : 'Download'}
                        </button>
                        <button
                          type="button"
                          className="sightingCard__actionsButton"
                          onClick={() => handleSendToWhatsApp(activeSighting)}
                          disabled={isSending || isDeleting || isHighlighting}
                        >
                          {isSending ? 'Sending…' : 'Send to WhatsApp'}
                        </button>
                        {activeSighting.mediaType === 'video' && (
                          <button
                            type="button"
                            className="sightingCard__actionsButton sightingCard__actionsButton--facebook"
                            onClick={() => handleOpenFacebookPublish(activeSighting, 'modal')}
                            disabled={isDeleting || isHighlighting}
                          >
                            <FaFacebookF />
                            Post to Facebook
                          </button>
                        )}
                        <button
                          type="button"
                          className="sightingCard__actionsButton sightingCard__actionsButton--alert"
                          onClick={() =>
                            handleSendToWhatsApp(activeSighting, {
                              alertStyle: 'emoji',
                              confirmationMessage: 'Send this sighting as an alert to WhatsApp groups?',
                            })
                          }
                          disabled={isSending || isDeleting || isHighlighting}
                        >
                          {isSending ? 'Sending…' : 'Alert'}
                        </button>
                        {isAdmin && (
                          <>
                            <button
                              type="button"
                              className="sightingCard__actionsButton sightingCard__actionsButton--danger"
                              onClick={() => handleDeleteSightings([activeSighting], { source: 'modal' })}
                              disabled={isDeleting || isHighlighting}
                            >
                              {isDeleting ? 'Deleting…' : 'Delete'}
                            </button>
                            <button
                              type="button"
                              className="sightingCard__actionsButton sightingCard__actionsButton--danger"
                              onClick={() => handleBulkDeleteSelected('modal')}
                              disabled={selectedSightingsCount === 0 || hasPendingSelectedDeletes}
                            >
                              {hasPendingSelectedDeletes
                                ? 'Deleting…'
                                : `Delete all selected (${selectedSightingsCount})`}
                            </button>
                          </>
                        )}
                      </div>
                      {sendStatus.state === 'success' && sendStatus.message && (
                        <span className="sightingModal__actionsMessage sightingModal__actionsMessage--success">
                          {sendStatus.message}
                        </span>
                      )}
                      {sendStatus.state === 'error' && sendStatus.message && (
                        <span className="sightingModal__actionsMessage sightingModal__actionsMessage--error">
                          {sendStatus.message}
                        </span>
                      )}
                      {isAdmin && highlightStatus.state === 'success' && highlightStatus.message && (
                        <span className="sightingModal__actionsMessage sightingModal__actionsMessage--success">
                          {highlightStatus.message}
                        </span>
                      )}
                      {isAdmin && highlightStatus.state === 'error' && highlightStatus.message && (
                        <span className="sightingModal__actionsMessage sightingModal__actionsMessage--error">
                          {highlightStatus.message}
                        </span>
                      )}
                      {isAdmin && deleteStatus.state === 'success' && deleteStatus.message && (
                        <span className="sightingModal__actionsMessage sightingModal__actionsMessage--success">
                          {deleteStatus.message}
                        </span>
                      )}
                      {isAdmin && deleteStatus.state === 'error' && deleteStatus.message && (
                        <span className="sightingModal__actionsMessage sightingModal__actionsMessage--error">
                          {deleteStatus.message}
                        </span>
                      )}
                    </div>
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      )}
      {facebookPublishTarget && selectedFacebookPage && (
        <FacebookVideoPublishModal
          key={facebookPublishTarget.id}
          entry={facebookPublishTarget}
          locationLabel={getLocationLabel(facebookPublishTarget.locationId)}
          page={selectedFacebookPage}
          onClose={() => setFacebookPublishTarget(null)}
          onPublished={handleFacebookPublished}
        />
      )}
    </div>
  );
}
